import styles from "./VolumeBundle.module.css";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import confetti from "canvas-confetti";
import BundlePreviewWrapper from "./BundlePreviewWrapper";
import { CustomSwatches, Swatches, VariantSelectorV2 } from "../../BundlePreviewWrapper/BundlePreviewWrapper";
import {
    AvailableVolumeTemplates,
    buildMetafieldFromSwatchOptions,
    getFinalPrice,
    getPriceLabel,
    DISCOUNT_TYPES,
    getDiscountValueWithSignStringSubscription,
    ATTRIBUTE_KEY_SHORTCODES,
    parseCTAWithIcon,
} from "../../constants";
import { OfferInterface, VariantsObj, VolumeConfigInterface, ProductObj } from "../../Types";
import { TStoreContext } from "../../../../context/types";
import { hexToRgba } from "../../../utilities";
import { FreeGiftComponent } from "./BundlePreviewFooter";
import useCustomLocation from "../../../../hooks/useCustomLocation";
import CountdownTimer from "../../../../components/CountdownTimer/CountdownTimer";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { getInitialSelectedVariants, getProductId } from "../../../constants";
import { useShopifyProductState } from "../../../../hooks/useShopifyProductState";
import PlusIcon from "../../../../components/Icons/PlusIcon";
import MinusIcon from "../../../../components/Icons/MinusIcon";
import DiscountTagBadge from "../../../../components/Badges/DiscountTagBadge";
import DiscountSkullBadge from "../../../../components/Badges/DiscountSkullBadge";
import DiscountRibbonBadge from "../../../../components/Badges/DiscountRibbonBadge";
import DiscountTicketBadge from "../../../../components/Badges/DiscountTicketBadge";
import DiscountFlagBadge from "../../../../components/Badges/DiscountFlagBadge";

const getParsedLabel = (
    text: string,
    { finalOriginalPrice, finalDiscountedPrice, combinedDiscountPercentage, variantCount, money_format, currency }: any,
) => {
    if (!text) return text;
    let newText = text;

    const savedTotal = finalOriginalPrice - finalDiscountedPrice;
    const savedPerItem = variantCount > 0 ? savedTotal / variantCount : 0;

    if (newText.includes("{saved_percentage}")) {
        newText = newText.replace(/{saved_percentage}/g, combinedDiscountPercentage + "%");
    }
    if (newText.includes("{saved_total}")) {
        newText = newText.replace(
            /{saved_total}/g,
            String(getPriceLabel(savedTotal, money_format, currency, false, "string") || ""),
        );
    }
    if (newText.includes("{saved_per_item}")) {
        newText = newText.replace(
            /{saved_per_item}/g,
            String(getPriceLabel(savedPerItem, money_format, currency, false, "string") || ""),
        );
    }

    return newText;
};

const SavingsSummaryBar = ({
    savingsSummary,
    bundlesDesign,
    savedAmount,
    savedPercentage,
    totalPrice,
    money_format,
    currency,
    bundleType,
}: {
    savingsSummary?: VolumeConfigInterface["savings_summary"];
    bundlesDesign?: VolumeConfigInterface["bundles_design"];
    savedAmount: number;
    savedPercentage: number;
    totalPrice: number;
    money_format: string;
    currency: any;
    bundleType?: string;
}) => {
    if (!savingsSummary?.status) return null;
    if (savingsSummary.hide_when_no_savings && savedAmount <= 0) return null;

    const fontSize = bundlesDesign?.savings_summary_font_size;
    const savedAmountLabel = String(getPriceLabel(savedAmount, money_format, currency, false, "string") || "");
    const totalPriceLabel = String(getPriceLabel(totalPrice, money_format, currency, false, "string") || "");
    const title = (savingsSummary.title || "")
        .replace(/{saved_amount}/g, savedAmountLabel)
        .replace(/{saved_percentage}/g, `${savedPercentage}%`)
        .replace(/{total_price}/g, totalPriceLabel);

    return (
        <InspectorEditableBlock bundleType={bundleType} settingKey="savingsSummary">
            <div
                className={styles.savingsSummaryBar}
                style={{
                    borderRadius: bundlesDesign?.savings_summary_border_radius,
                    textAlign: savingsSummary.text_align,
                    color: bundlesDesign?.savings_summary_text_color,
                    background: bundlesDesign?.savings_summary_background_color,
                    fontSize,
                    fontWeight: bundlesDesign?.savings_summary_font_style?.split(" ")[0],
                    fontStyle: bundlesDesign?.savings_summary_font_style?.split(" ")[1],
                }}
            >
                {parseCTAWithIcon(title, savingsSummary.icon_url, fontSize, savingsSummary.icon_type || "none", true)}
            </div>
        </InspectorEditableBlock>
    );
};

const ProductBundleItem = ({
    index,
    _pd,
    standAlone,
    bundleConfig,
    updateProductVariants,
    money_format,
    currency,
    isCurrentActiveOffer,
    bundles_design,
    isHorizontal,
}: {
    index: number;
    _pd: ProductObj;
    standAlone: boolean;
    bundleConfig: VolumeConfigInterface;
    updateProductVariants?: any;
    money_format: string;
    currency: any;
    isCurrentActiveOffer: boolean;
    bundles_design: VolumeConfigInterface["bundles_design"];
    isHorizontal: boolean;
}) => {
    const [selectedVariants, setSelectedVariants] = useState<Array<VariantsObj>>(
        new Array(_pd.quantity).fill(_pd.variants.find((vv: any) => vv.availableForSale == true) || _pd.variants[0]),
    );
    const [lastChangedIndex, setLastChangedIndex] = useState(0);

    const handleVariantsChange = (i: number) => (updated: VariantsObj[]) => {
        setLastChangedIndex(i);
        setSelectedVariants(updated);
    };

    useEffect(() => {
        const updatedSelectedVariants = selectedVariants?.map((variant: any) => ({
            ...variant,
        }));
        _pd.selectedVariants = updatedSelectedVariants;
        updateProductVariants(index, updatedSelectedVariants);
    }, [selectedVariants]);

    useEffect(() => {
        const svs = new Array(_pd.quantity).fill(
            _pd.variants.find((vv: any) => vv.availableForSale == true) || _pd.variants[0],
        );
        setSelectedVariants(svs);
        setLastChangedIndex(0);
    }, [_pd]);

    const variantImage =
        selectedVariants[lastChangedIndex]?.image || selectedVariants.find((v: any) => v?.image)?.image;
    const imageObj = variantImage || _pd?.images?.[0];
    const image = imageObj ? imageObj?.originalSrc || (imageObj as any).src : "";

    const isProductAvailable = useMemo(() => {
        return _pd.variants.some(
            (vv: any) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
        );
    }, [_pd]);

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product: _pd,
        bundleConfig: bundleConfig,
    });

    const originalPrice = selectedVariants.reduce((acc, v) => acc + Number(v?.price || 0), 0);
    const originalComparePrice = selectedVariants.reduce((acc, v) => acc + Number(v?.compareAtPrice || v?.price), 0);
    const discountedPrice = getFinalPrice(
        _pd.discountType,
        _pd.discountValue,
        bundleConfig?.apply_discount_on_compare_price ? originalComparePrice : originalPrice,
    );

    return (
        <>
            <React.Fragment key={_pd.id}>
                <div className={isHorizontal ? styles.plusSeparatorHorizontal : styles.plusSeparatorVertical}>
                    <div
                        className={styles.plusLine}
                        style={{
                            backgroundColor: hexToRgba(bundles_design?.product_border_color, 0.6),
                        }}
                    ></div>
                    <div
                        className={styles.plusIcon}
                        style={{
                            border: `1px solid ${hexToRgba(bundles_design?.product_border_color, 0.6)}`,
                        }}
                    >
                        <PlusIcon color={bundles_design?.product_border_color} />
                    </div>
                    <div
                        className={styles.plusLine}
                        style={{
                            backgroundColor: hexToRgba(bundles_design?.product_border_color, 0.6),
                        }}
                    ></div>
                </div>

                <div className={styles.bundleItem}>
                    <div className={styles.bundleItemImgContainer}>
                        <img src={image} alt={_pd.title} className={styles.bundleItemImg} />
                        {_pd.quantity > 1 && <span className={styles.bundleItemQuantity}>x{_pd.quantity}</span>}
                    </div>
                    <div className={styles.bundleItemInfo}>
                        <InspectorEditableBlock
                            bundleType={bundleConfig?.bundle_type}
                            settingKey="productTitle"
                            wrapWithDiv={true}
                        >
                            <div
                                className={styles.bundleItemTitle}
                                style={{
                                    fontSize: bundles_design.product_name_size,
                                    color: bundles_design?.product_name_color,
                                    fontWeight: bundles_design?.product_name_style?.split(" ")[0],
                                    fontStyle: bundles_design?.product_name_style?.split(" ")[1],
                                }}
                            >
                                {_pd.title}
                            </div>
                        </InspectorEditableBlock>
                        {isProductAvailable ? (
                            <div className={styles.bundleItemPriceRow}>
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="productPrice"
                                >
                                    <span
                                        className={styles.bundleItemPrice}
                                        style={{
                                            color: bundles_design.highlighted_price_tag_color,
                                            fontSize: bundles_design.highlighted_price_tag_size,
                                            fontWeight: bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                            fontStyle: bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                        }}
                                    >
                                        {discountedPrice <= 0
                                            ? bundleConfig.free_text || "Free"
                                            : getPriceLabel(discountedPrice, money_format, currency)}
                                    </span>
                                </InspectorEditableBlock>

                                {discountedPrice <
                                    (bundleConfig?.use_compare_at_price ? originalComparePrice : originalPrice) && (
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="productCompareAtPrice"
                                    >
                                        <span
                                            className={styles.bundleItemComparePrice}
                                            style={{
                                                color: bundles_design.product_price_color,
                                                fontSize: bundles_design.product_price_size,
                                                fontWeight: bundles_design.product_price_style?.split(" ")[0],
                                                fontStyle: bundles_design.product_price_style?.split(" ")[1],
                                            }}
                                        >
                                            {getPriceLabel(
                                                bundleConfig?.use_compare_at_price
                                                    ? originalComparePrice
                                                    : originalPrice,
                                                money_format,
                                                currency,
                                            )}
                                        </span>
                                    </InspectorEditableBlock>
                                )}
                            </div>
                        ) : (
                            <span className={styles.outOfStock} style={{ textAlign: isHorizontal ? "center" : "left" }}>
                                Out of stock!
                            </span>
                        )}

                        {isCurrentActiveOffer && _pd?.variants?.length > 1 && isProductAvailable && (
                            <div className={styles.bundleVariantList}>
                                {selectedVariants.map((_: any, i: number) =>
                                    userShopname === "typsy-beauty.myshopify.com" ? (
                                        <Swatches
                                            bundlesDesign={bundles_design}
                                            key={i}
                                            variants={_pd.variants}
                                            handleChange={handleVariantsChange(i)}
                                            selected={selectedVariants}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            options={_pd.options || []}
                                        />
                                    ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                        <CustomSwatches
                                            key={i}
                                            variants={_pd.variants}
                                            handleChange={handleVariantsChange(i)}
                                            selected={selectedVariants}
                                            options={_pd.options || []}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            metafield={effectiveMetafield}
                                            bundlesDesign={bundles_design}
                                            bundleConfig={bundleConfig}
                                            hideLabel={!!bundleConfig?.show_option_labels_once && i !== 0}
                                            hideSerialNumber={!!bundleConfig?.hide_item_numbers}
                                        />
                                    ) : (
                                        <VariantSelectorV2
                                            key={i}
                                            variants={_pd.variants}
                                            handleChange={handleVariantsChange(i)}
                                            selected={selectedVariants}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            bundlesDesign={bundles_design}
                                            options={_pd.options || []}
                                            metafield={effectiveMetafield}
                                            bundleConfig={bundleConfig}
                                            hideLabel={!!bundleConfig?.show_option_labels_once && i !== 0}
                                            hideSerialNumber={!!bundleConfig?.hide_item_numbers}
                                        />
                                    ),
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </React.Fragment>
        </>
    );
};

export const ProductBundleComponent = ({
    products,
    bundleConfig,
    standAlone,
    setBundleProductSelectedVariants = null,
    active,
    index,
    money_format,
    currency,
    offer,
    mainProduct,
    mainProductImage,
    mainProductOriginalPrice,
    mainProductDiscountedPrice,
    renderVariantSelector,
    isCurrentActiveOffer,
}: {
    products: ProductObj[];
    bundleConfig: VolumeConfigInterface;
    standAlone: boolean;
    setBundleProductSelectedVariants?: any | null;
    active: number;
    index: number;
    money_format: string;
    currency: any;
    offer: OfferInterface;
    mainProduct: ProductObj;
    mainProductImage?: string;
    mainProductOriginalPrice: number;
    mainProductDiscountedPrice: number;
    renderVariantSelector: any;
    isCurrentActiveOffer: boolean;
}) => {
    const [_products, setProducts] = useState<any>([]);
    const isHorizontal = offer.bundle_layout === "horizontal";
    const isHorizontalTemplate =
        bundleConfig.selected_template_volume == AvailableVolumeTemplates.HORIZONTAL_BASIC ||
        bundleConfig.selected_template_volume == AvailableVolumeTemplates.HORIZONTAL_MODERN;

    useEffect(() => {
        setProducts(products);
    }, [products]);

    useEffect(() => {
        if (setBundleProductSelectedVariants && isCurrentActiveOffer) {
            setBundleProductSelectedVariants(() => {
                const temp: any[] = [];
                products.forEach((pd: ProductObj) => {
                    const sv = pd?.selectedVariants || [];
                    temp.push(...sv);
                });
                return temp;
            });
        }
    }, [_products, index, active]);

    const updateProductVariants = (index: number, selectedVariants: VariantsObj[]) => {
        if (selectedVariants.length) {
            setProducts((ps: ProductObj[]) => {
                const temp: any[] = [...ps];
                temp[index].selectedVariants = selectedVariants;
                return temp;
            });
        }
    };

    return (
        <div className={styles.bundleProductComponent}>
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCard">
                <div
                    className={isHorizontal ? styles.bundleContainerHorizontal : styles.bundleContainerVertical}
                    style={{
                        ...(isHorizontalTemplate && {
                            maxWidth: "200px",
                        }),
                        border: `1px solid ${hexToRgba(bundleConfig?.bundles_design?.product_border_color, 0.6)}`,
                    }}
                >
                    {/* MAIN PRODUCT */}
                    <div className={styles.bundleItem}>
                        <div className={styles.bundleItemImgContainer}>
                            <img
                                src={mainProductImage || mainProduct?.images?.[0]?.originalSrc}
                                alt={mainProduct.title}
                                className={styles.bundleItemImg}
                            />
                            {offer.quantity > 1 && <span className={styles.bundleItemQuantity}>x{offer.quantity}</span>}
                        </div>
                        <div className={styles.bundleItemInfo}>
                            <InspectorEditableBlock
                                bundleType={bundleConfig?.bundle_type}
                                settingKey="productTitle"
                                wrapWithDiv={true}
                            >
                                <div
                                    className={styles.bundleItemTitle}
                                    style={{
                                        fontSize: bundleConfig?.bundles_design.product_name_size,
                                        color: bundleConfig?.bundles_design?.product_name_color,
                                        fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                                        fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                                    }}
                                >
                                    {mainProduct.title}
                                </div>
                            </InspectorEditableBlock>

                            <div className={styles.bundleItemPriceRow}>
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="productPrice"
                                >
                                    <span
                                        className={styles.bundleItemPrice}
                                        style={{
                                            color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                            fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                                            fontWeight:
                                                bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                            fontStyle:
                                                bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                        }}
                                    >
                                        {mainProductDiscountedPrice <= 0
                                            ? bundleConfig.free_text || "Free"
                                            : getPriceLabel(mainProductDiscountedPrice, money_format, currency)}
                                    </span>
                                </InspectorEditableBlock>
                                {mainProductDiscountedPrice < mainProductOriginalPrice && (
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="productCompareAtPrice"
                                    >
                                        <span
                                            className={styles.bundleItemComparePrice}
                                            style={{
                                                color: bundleConfig?.bundles_design.product_price_color,
                                                fontSize: bundleConfig?.bundles_design.product_price_size,
                                                fontWeight:
                                                    bundleConfig?.bundles_design.product_price_style?.split(" ")[0],
                                                fontStyle:
                                                    bundleConfig?.bundles_design.product_price_style?.split(" ")[1],
                                            }}
                                        >
                                            {getPriceLabel(mainProductOriginalPrice, money_format, currency)}
                                        </span>
                                    </InspectorEditableBlock>
                                )}
                            </div>
                            {/* variant selector of main product */}
                            {renderVariantSelector({ padding: "0" })}
                        </div>
                    </div>
                    {/* Bundle Product Items */}
                    {_products?.map((_pd: any, index: number) => (
                        <ProductBundleItem
                            index={index}
                            _pd={_pd}
                            standAlone={standAlone}
                            bundleConfig={bundleConfig}
                            bundles_design={bundleConfig.bundles_design}
                            updateProductVariants={updateProductVariants}
                            key={_pd.id}
                            currency={currency}
                            money_format={money_format}
                            isCurrentActiveOffer={isCurrentActiveOffer}
                            isHorizontal={isHorizontal}
                        />
                    ))}
                </div>
            </InspectorEditableBlock>
        </div>
    );
};

const handleActive = (
    idx = -1,
    setIdx: any = () => {},
    resetFreeGift: any = () => {},
    resetBundleProduct: any = () => {},
    resetUpsellProduct: any = () => {},
    offer?: OfferInterface,
    originEl?: HTMLElement | null,
    showPopper: boolean = false,
) => {
    if (idx == -1) {
        return;
    }
    setIdx(idx);
    resetFreeGift([]);
    resetBundleProduct([]);
    resetUpsellProduct([]);

    if (offer?.allow_free_gifts && showPopper) {
        const bundleElement = document.getElementById("uniqueProductsKefi");
        const referenceEl = bundleElement || originEl;

        let originX = 0.5;
        let originY = 0.5;

        if (referenceEl) {
            const rect = referenceEl.getBoundingClientRect();
            originX = (rect.left + rect.width / 2) / window.innerWidth;
            originY = rect.top / window.innerHeight;
        }

        confetti({
            particleCount: 200,
            spread: 70,
            origin: { x: originX, y: originY },
            zIndex: 9999,
        });
    }
};

const UpsellItem = ({
    index,
    _up,
    bundleConfig,
    money_format,
    currency,
    standAlone,
    updateUpsellVariants,
    isCurrentActiveOffer,
    isHorizontalTemplate,
}: {
    index: number;
    _up: ProductObj;
    bundleConfig: VolumeConfigInterface;
    money_format: string;
    currency: any;
    standAlone: boolean;
    updateUpsellVariants: (index: number, variants: VariantsObj[]) => void;
    isCurrentActiveOffer: boolean;
    isHorizontalTemplate: boolean;
}) => {
    const bundles_design = bundleConfig.bundles_design;
    const [selectedVariants, setSelectedVariants] = useState<VariantsObj[]>(
        new Array(_up.quantity || 1).fill(_up.variants.find((v: any) => v.availableForSale) || _up.variants[0]),
    );
    const [lastChangedIndex, setLastChangedIndex] = useState(0);

    const handleVariantsChange = (updated: VariantsObj[]) => {
        const changedIndex = updated.findIndex((v, i) => v !== selectedVariants[i]);
        if (changedIndex !== -1) {
            setLastChangedIndex(changedIndex);
        }
        setSelectedVariants(updated);
    };

    useEffect(() => {
        const updatedSelectedVariants = selectedVariants?.map((variant: any) => ({
            ...variant,
        }));
        _up.selectedVariants = updatedSelectedVariants;
        updateUpsellVariants(index, updatedSelectedVariants);
    }, [selectedVariants]);

    useEffect(() => {
        const sv = new Array(_up.quantity || 1).fill(
            _up.variants.find((v: any) => v.availableForSale) || _up.variants[0],
        );
        setSelectedVariants(sv);
        setLastChangedIndex(0);
    }, [_up]);

    const isProductAvailable = useMemo(() => {
        return _up.variants.some(
            (vv: any) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
        );
    }, [_up]);

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({ product: _up, bundleConfig });

    // this is being used to round the numbers, cause js is sometimes returing numbers like 95.000000001 (it is causing UI issues)
    const round2 = (n: number) => Math.round(n * 100) / 100;

    const originalPrice = round2(selectedVariants.reduce((acc, v) => acc + Number(v?.price || 0), 0));
    const originalComparePrice = round2(
        selectedVariants.reduce((acc, v) => acc + Number(v?.compareAtPrice || v?.price || 0), 0),
    );
    const discountedPrice = getFinalPrice(
        _up.discountType || "none",
        _up.discountValue || 0,
        bundleConfig?.apply_discount_on_compare_price ? originalComparePrice : originalPrice,
    );

    // const variantImage = selectedVariants.find((v: any) => v?.image)?.image;
    const variantImage =
        selectedVariants[lastChangedIndex]?.image || selectedVariants.find((v: any) => v?.image)?.image;
    const imageObj = variantImage || _up.images?.[0];
    const image = imageObj ? imageObj.originalSrc || (imageObj as any).src : "";

    // Resolve upsell text placeholders per-product
    let displayText = _up?.upsell_text || "";
    if (displayText.includes("{discount}")) {
        if (_up.discountType === "percentage") {
            displayText = displayText.replace(/{discount}/g, _up.discountValue + "%");
        } else {
            const valStr = getDiscountValueWithSignStringSubscription(
                _up.discountType as any,
                _up.discountValue || 0,
                currency,
                money_format,
            );
            displayText = displayText.replace(/{discount}/g, valStr);
        }
    }
    if (displayText.includes("{product_title}")) {
        displayText = displayText.replace(/{product_title}/g, _up.title);
    }

    return (
        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="upsellProduct">
            <div
                style={{
                    display: "flex",
                    flexDirection: isHorizontalTemplate ? "column" : "row",
                    alignItems: "center",
                    gap: 10,
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {/* Product image + quantity badge */}
                <div className={styles.upsellItemImgContainer}>
                    {image && <img src={image} alt={_up.title} className={styles.upsellItemImg} />}
                    {(_up.quantity || 1) > 1 && <span className={styles.upsellItemQuantity}>x{_up.quantity}</span>}
                </div>

                {/* Info: upsell text + title */}
                <div className={isHorizontalTemplate ? styles.upsellInfoVerticalFormat : styles.upsellInfo}>
                    {/* Left Side */}
                    <div className={styles.upsellLeft}>
                        <span
                            className={styles.upsellText}
                            style={{
                                display: displayText ? "inline-block" : "none",
                                fontSize: bundles_design.upsell_text_size,
                                color: bundles_design?.upsell_text_color,
                                fontWeight: bundles_design?.upsell_text_style?.split(" ")[0],
                                fontStyle: bundles_design?.upsell_text_style?.split(" ")[1],
                                textAlign: isHorizontalTemplate ? "center" : "left",
                            }}
                            dangerouslySetInnerHTML={{ __html: displayText }}
                        ></span>

                        {isCurrentActiveOffer && _up?.variants?.length > 1 && isProductAvailable && (
                            <div className={styles.variantWrapper}>
                                {selectedVariants.map((_: any, i: number) =>
                                    userShopname === "typsy-beauty.myshopify.com" ? (
                                        <Swatches
                                            key={i}
                                            bundlesDesign={bundles_design}
                                            variants={_up.variants}
                                            handleChange={handleVariantsChange}
                                            selected={selectedVariants}
                                            options={_up.options || []}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                        />
                                    ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                        <CustomSwatches
                                            key={i}
                                            variants={_up.variants}
                                            handleChange={handleVariantsChange}
                                            selected={selectedVariants}
                                            options={_up.options || []}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            metafield={effectiveMetafield}
                                            bundlesDesign={bundles_design}
                                            bundleConfig={bundleConfig}
                                            hideLabel={!!bundleConfig?.show_option_labels_once && i !== 0}
                                            hideSerialNumber={!!bundleConfig?.hide_item_numbers}
                                        />
                                    ) : (
                                        <VariantSelectorV2
                                            key={i}
                                            variants={_up.variants}
                                            handleChange={handleVariantsChange}
                                            selected={selectedVariants}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            bundlesDesign={bundles_design}
                                            options={_up.options || []}
                                            metafield={effectiveMetafield}
                                            bundleConfig={bundleConfig}
                                            hideLabel={!!bundleConfig?.show_option_labels_once && i !== 0}
                                            hideSerialNumber={!!bundleConfig?.hide_item_numbers}
                                        />
                                    ),
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right Side */}
                    <div className={styles.upsellRight}>
                        {isProductAvailable ? (
                            <div className={styles.upsellPricing}>
                                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="upsellPrice">
                                    <span
                                        className={styles.upsellDiscountedPrice}
                                        style={{
                                            color: bundles_design.upsell_price_color,
                                            fontSize: bundles_design.upsell_price_size,
                                            fontWeight: bundles_design.upsell_price_style?.split(" ")[0],
                                            fontStyle: bundles_design.upsell_price_style?.split(" ")[1],
                                        }}
                                    >
                                        {discountedPrice <= 0
                                            ? bundleConfig.free_text || "Free"
                                            : getPriceLabel(discountedPrice, money_format, currency)}
                                    </span>
                                </InspectorEditableBlock>

                                {discountedPrice <
                                    (bundleConfig?.use_compare_at_price ? originalComparePrice : originalPrice) && (
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="upsellCompareAtPrice"
                                    >
                                        <span
                                            className={styles.upsellOriginalPrice}
                                            style={{
                                                color: bundles_design.upsell_compare_price_color,
                                                fontSize: bundles_design.upsell_compare_price_size,
                                                fontWeight: bundles_design.upsell_compare_price_style?.split(" ")[0],
                                                fontStyle: bundles_design.upsell_compare_price_style?.split(" ")[1],
                                            }}
                                        >
                                            {getPriceLabel(
                                                bundleConfig?.use_compare_at_price
                                                    ? originalComparePrice
                                                    : originalPrice,
                                                money_format,
                                                currency,
                                            )}
                                        </span>
                                    </InspectorEditableBlock>
                                )}
                            </div>
                        ) : (
                            <span className={styles.outOfStock}>{bundleConfig?.no_stock_text || "Out of stock!"}</span>
                        )}
                    </div>
                </div>
            </div>
        </InspectorEditableBlock>
    );
};

export const UpsellComponent = ({
    products,
    offer,
    bundleConfig,
    standAlone,
    money_format,
    currency,
    isCurrentActiveOffer,
    setUpsellSelectedVariants,
    active,
}: {
    products: ProductObj[];
    offer: OfferInterface;
    bundleConfig: VolumeConfigInterface;
    standAlone: boolean;
    money_format: string;
    currency: any;
    isCurrentActiveOffer: boolean;
    setUpsellSelectedVariants?: Dispatch<SetStateAction<VariantsObj[]>>;
    active: number;
}) => {
    const isHorizontalTemplate =
        bundleConfig.selected_template_volume == AvailableVolumeTemplates.HORIZONTAL_BASIC ||
        bundleConfig.selected_template_volume == AvailableVolumeTemplates.HORIZONTAL_MODERN;

    const [_products, setProducts] = useState<ProductObj[]>([]);
    // Track which products are checked by their index
    const [checkedIndexes, setCheckedIndexes] = useState<number[]>([]);

    useEffect(() => {
        if (offer?.upsell_products?.length) {
            const checkedIndexes = offer.upsell_products.reduce((acc: number[], curP: ProductObj, i: number) => {
                if (curP.checked) {
                    acc.push(i);
                }
                return acc;
            }, []);
            setCheckedIndexes(checkedIndexes);
        }
    }, [bundleConfig.volume_discounts]);

    useEffect(() => {
        setProducts(products);
    }, [products]);

    useEffect(() => {
        if (setUpsellSelectedVariants && isCurrentActiveOffer) {
            setUpsellSelectedVariants(() => {
                const temp: VariantsObj[] = [];
                checkedIndexes.forEach((i) => {
                    const pd = _products[i];
                    const sv = pd?.selectedVariants || [];
                    temp.push(...sv);
                });
                return temp;
            });
        }
    }, [_products, checkedIndexes, active]);

    const updateUpsellVariants = (index: number, selectedVariants: VariantsObj[]) => {
        if (selectedVariants.length) {
            setProducts((ps: any) => {
                const temp: any[] = [...ps];
                temp[index].selectedVariants = selectedVariants;
                return temp;
            });
        }
    };

    const toggleChecked = (e: React.MouseEvent | React.ChangeEvent, i: number) => {
        e.stopPropagation();
        setCheckedIndexes((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
    };

    return (
        <div onClick={(e) => e.stopPropagation()}>
            {_products.map((_up, i) => (
                <div
                    key={_up.id + i}
                    className={isHorizontalTemplate ? styles.upsellRowVerticalFormat : styles.upsellRow}
                    style={{
                        borderTop: `1px dashed ${hexToRgba(bundleConfig?.bundles_design?.product_border_color, 0.5)}`,
                        ...(_up.visible_when_bar_selected &&
                            !isCurrentActiveOffer && {
                                display: "none",
                            }),
                        ...(isCurrentActiveOffer && {
                            backgroundColor: bundleConfig.bundles_design?.upsell_bg_color,
                        }),
                    }}
                >
                    <input
                        type="checkbox"
                        checked={checkedIndexes.includes(i)}
                        onChange={(e) => toggleChecked(e, i)}
                        onClick={(e) => e.stopPropagation()}
                        className={styles.upsellCheckbox}
                    />
                    <UpsellItem
                        index={i}
                        _up={_up}
                        bundleConfig={bundleConfig}
                        money_format={money_format}
                        currency={currency}
                        standAlone={standAlone}
                        updateUpsellVariants={updateUpsellVariants}
                        isCurrentActiveOffer={isCurrentActiveOffer}
                        isHorizontalTemplate={isHorizontalTemplate}
                    />
                </div>
            ))}
        </div>
    );
};

// Subscription selection box shown below the bundle offer cards
const SubscriptionBox = ({
    product,
    selectedSellingPlan,
    setSelectedSellingPlan,
    bundleConfig,
    activeSelectedVariants,
}: {
    product: ProductObj | null | undefined;
    selectedSellingPlan: string;
    setSelectedSellingPlan: Dispatch<SetStateAction<string>>;
    bundleConfig: VolumeConfigInterface;
    activeSelectedVariants: VariantsObj[];
}) => {
    const subscriptions = useMemo(() => {
        const productSubscriptions = product?.subscriptions || [];
        if (!activeSelectedVariants || activeSelectedVariants.length === 0) {
            return productSubscriptions;
        }

        // Compute intersection of sellingPlanIds across all selected variants
        const commonSellingPlanIds = activeSelectedVariants.reduce<Set<string>>((acc, variant, index) => {
            const variantPlans = new Set(variant.sellingPlanIds || []);
            if (index === 0) return variantPlans;
            return new Set([...acc].filter((id) => variantPlans.has(id)));
        }, new Set<string>());

        return productSubscriptions.filter((sub) => commonSellingPlanIds.has(sub.id));
    }, [product?.subscriptions, activeSelectedVariants]);

    if (!bundleConfig.enable_subscription_integration || !subscriptions || subscriptions.length === 0) return null;

    const isSubscribed = !!selectedSellingPlan;

    return (
        <div
            className={styles.subscriptionBox}
            style={{
                border: `1px solid ${bundleConfig?.bundles_design?.product_border_color || "#e0e0e0"}`,
                borderRadius: bundleConfig?.bundles_design?.border_radius || 8,
                fontSize: bundleConfig?.bundles_design?.product_name_size || 14,
            }}
        >
            {/* One-time purchase row */}
            <div
                onClick={() => setSelectedSellingPlan("")}
                className={styles.subscriptionRow}
                style={{
                    backgroundColor: !isSubscribed
                        ? bundleConfig?.bundles_design?.product_background_color || "#f5f5f5"
                        : "transparent",
                    borderBottom: `1px solid ${bundleConfig?.bundles_design?.product_border_color || "#e0e0e0"}`,
                }}
            >
                <span
                    className={`${styles.customChecked} ${!isSubscribed && styles._checked}`}
                    style={{
                        background: !isSubscribed ? bundleConfig?.bundles_design?.product_border_color : "transparent",
                        outlineColor: bundleConfig?.bundles_design?.product_border_color,
                        width: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                        height: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                        minWidth: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                        minHeight: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                    }}
                ></span>
                <span style={{ color: bundleConfig?.bundles_design?.product_name_color }}>One-time purchase</span>
            </div>

            {/* Subscribe row */}
            <div
                onClick={() => {
                    if (!isSubscribed) {
                        // default to first subscription
                        setSelectedSellingPlan(subscriptions[0].id);
                    }
                }}
                className={styles.subscriptionRow}
                style={{
                    alignItems: isSubscribed ? "flex-start" : "center",
                    backgroundColor: isSubscribed
                        ? bundleConfig?.bundles_design?.product_background_color || "#f5f5f5"
                        : "transparent",
                }}
            >
                <span
                    className={`${styles.customChecked} ${isSubscribed && styles._checked}`}
                    style={{
                        background: isSubscribed ? bundleConfig?.bundles_design?.product_border_color : "transparent",
                        outlineColor: bundleConfig?.bundles_design?.product_border_color,
                        width: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                        height: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                        minWidth: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                        minHeight: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                        marginTop: isSubscribed ? "4px" : "0",
                    }}
                ></span>
                <div className={styles.subscriptionRightCol}>
                    <span style={{ color: bundleConfig?.bundles_design?.product_name_color }}>
                        Subscribe &amp; Save
                    </span>
                    {isSubscribed && (
                        <select
                            value={selectedSellingPlan}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setSelectedSellingPlan(e.target.value)}
                            className={styles.subscriptionSelect}
                            style={{
                                border: `1px solid ${bundleConfig?.bundles_design?.product_border_color || "#ccc"}`,
                                fontSize: bundleConfig?.bundles_design?.product_name_size || 14,
                                color: bundleConfig?.bundles_design?.product_name_color,
                            }}
                        >
                            {subscriptions.map((sub: any) => {
                                const discountText =
                                    sub.discountType === "percentage" && sub.discountValue > 0
                                        ? ` (${sub.discountValue}% off)`
                                        : sub.discountType === "flat" && sub.discountValue > 0
                                          ? ` (${sub.discountValue} off)`
                                          : sub.discountType === "fixed" && sub.discountValue > 0
                                            ? ` (${sub.discountValue} price)`
                                            : "";
                                return (
                                    <option key={sub.id} value={sub.id}>
                                        {sub.name}
                                        {discountText}
                                    </option>
                                );
                            })}
                        </select>
                    )}
                </div>
            </div>
        </div>
    );
};

const QBQuantitySelector = ({
    setSelectedVariants = () => {},
    selectedVariants,
    minQuantity = 1,
    bundleConfig,
}: {
    minQuantity: number;
    selectedVariants: any;
    setSelectedVariants: Dispatch<SetStateAction<VariantsObj[]>>;
    bundleConfig: VolumeConfigInterface;
}) => {
    const isHorizontalTemplate =
        bundleConfig.selected_template_volume == AvailableVolumeTemplates.HORIZONTAL_BASIC ||
        bundleConfig.selected_template_volume == AvailableVolumeTemplates.HORIZONTAL_MODERN;
    const updateQuantity = (change: number) => {
        let updatedSv: VariantsObj[] = [];
        setSelectedVariants((prevSv) => {
            // selectedVariants updation
            updatedSv = [...prevSv];
            if (change == +1) {
                updatedSv.push(updatedSv[updatedSv?.length - 1]);
            } else {
                updatedSv.pop();
            }

            return updatedSv;
        });
    };
    return (
        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productQuantitySelector">
            <div
                className={styles.subInfo}
                style={{
                    ...(isHorizontalTemplate && { margin: "auto" }),
                    ...(bundleConfig?.selected_template_volume == AvailableVolumeTemplates.VERTICAL_MODERN && {
                        marginRight: "auto",
                        marginLeft: 0,
                    }),
                }}
            >
                {!!bundleConfig?.quantity_text?.trim()?.length && (
                    <div
                        className={styles.qtyLabel}
                        style={{
                            color: bundleConfig?.bundles_design?.quantity_selector_text_color,
                            fontSize: bundleConfig?.bundles_design?.quantity_selector_text_size,
                            fontWeight: bundleConfig?.bundles_design?.quantity_selector_text_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design?.quantity_selector_text_style?.split(" ")[1],
                        }}
                    >
                        {bundleConfig.quantity_text}
                    </div>
                )}
                <div
                    className={styles.qbQuantitySelectorWrapper}
                    style={{
                        color: bundleConfig?.bundles_design?.quantity_selector_text_color,
                        borderColor: bundleConfig?.bundles_design?.quantity_selector_border_color,
                        backgroundColor: bundleConfig?.bundles_design?.quantity_selector_bg_color,
                        borderRadius: bundleConfig?.bundles_design?.quantity_selector_border_radius,
                        borderWidth: bundleConfig?.bundles_design?.quantity_selector_border_width,
                    }}
                >
                    <button
                        disabled={selectedVariants.length <= minQuantity}
                        className={styles.quantityButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            updateQuantity(-1);
                        }}
                    >
                        <MinusIcon
                            size={bundleConfig?.bundles_design?.quantity_selector_text_size}
                            color={bundleConfig?.bundles_design?.quantity_selector_text_color}
                        />
                    </button>
                    <span
                        className={styles.qtyCount}
                        style={{
                            color: bundleConfig?.bundles_design?.quantity_selector_text_color,
                            fontSize: bundleConfig?.bundles_design?.quantity_selector_text_size,
                            fontWeight: bundleConfig?.bundles_design?.quantity_selector_text_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design?.quantity_selector_text_style?.split(" ")[1],
                        }}
                    >
                        {selectedVariants?.length}
                    </span>
                    <button
                        className={styles.quantityButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            updateQuantity(+1);
                        }}
                    >
                        <PlusIcon
                            size={bundleConfig?.bundles_design?.quantity_selector_text_size}
                            color={bundleConfig?.bundles_design?.quantity_selector_text_color}
                        />
                    </button>
                </div>
            </div>
        </InspectorEditableBlock>
    );
};

const DISCOUNT_LABEL_BADGES = {
    ribbon: DiscountRibbonBadge,
    ticket: DiscountTicketBadge,
    tag: DiscountTagBadge,
    skull: DiscountSkullBadge,
    flag: DiscountFlagBadge,
};

const renderDiscountLabelContent = (
    offer: OfferInterface | undefined,
    displayDiscountLabel: string,
    bundleConfig: VolumeConfigInterface,
    centerAlign: boolean = false,
) => {
    const type = offer?.discount_label_type;

    if (type === "custom") {
        if (offer?.discount_label_custom_img.length == 0) return;

        return (
            <img
                src={offer?.discount_label_custom_img}
                alt=""
                className={styles.discountLabelCustomImg}
                style={{
                    width: "auto",
                    height: offer?.discount_label_custom_img_size,
                    objectFit: offer?.discount_label_custom_img_fit || "contain",
                    borderRadius: offer?.discount_label_custom_img_radius || 0,
                    ...(centerAlign
                        ? { left: "50%", transform: "translate(-50%, -50%)" }
                        : { right: 11, transform: "translateY(-50%)" }),
                }}
            />
        );
    }

    const BadgeComponent = type && DISCOUNT_LABEL_BADGES[type as keyof typeof DISCOUNT_LABEL_BADGES];
    if (BadgeComponent) {
        return (
            <BadgeComponent
                text={displayDiscountLabel}
                color={bundleConfig?.bundles_design.discount_label_color}
                fontWeight={bundleConfig?.bundles_design.discount_label_style?.split(" ")[0]}
                fontStyle={bundleConfig?.bundles_design.discount_label_style?.split(" ")[1]}
                fontSize={bundleConfig?.bundles_design.discount_label_size}
                primaryColor={bundleConfig?.bundles_design.discount_label_primary_color}
                secondaryColor={bundleConfig?.bundles_design.discount_label_secondary_color}
                {...(centerAlign ? { style: { left: "50%", right: "auto", transform: "translateX(-50%)" } } : {})}
            />
        );
    }

    return displayDiscountLabel;
};

const VolumeBundle = ({ bundleConfig, targetForm }: { bundleConfig: VolumeConfigInterface; targetForm?: any }) => {
    return (
        !!bundleConfig && (
            <VolumePreview
                bundleConfig={bundleConfig}
                standAlone={true}
                pId={0}
                appConfigContext={
                    {
                        currency: window.kefi_bundles.localization.currency,
                        money_format: window.kefi_bundles.money_format,
                    } as TStoreContext
                }
                targetForm={targetForm}
            />
        )
    );
};

type Props_VolumePreview = {
    bundleConfig: VolumeConfigInterface;
    standAlone?: boolean;
    pId: string | number;
    vId?: string | number;
    appConfigContext: TStoreContext;
    targetForm?: HTMLElement | null;
    setActiveQuantity?: Dispatch<React.SetStateAction<number>>;
};

export const OfferCardShimmer = () => {
    return <div className={styles.offerCardShimmer}>Select product(s)</div>;
};

const getHoverEffectStyle = (
    bundlesDesign: VolumeConfigInterface["bundles_design"] | undefined,
    isHovered: boolean,
): React.CSSProperties => {
    const effect = bundlesDesign?.hover_effect;
    if (!isHovered || !effect || effect === "none") return {};
    const color = bundlesDesign?.hover_color || "#000000";
    const intensity = (bundlesDesign?.hover_intensity ?? 20) / 100;
    const size = bundlesDesign?.hover_size ?? 12;
    const predefinedStyles = { transition: "transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease" };
    switch (effect) {
        case "box_shadow":
            return { ...predefinedStyles, boxShadow: `0px 4px ${size}px 0px ${hexToRgba(color, intensity)}` };
        case "lift":
            return {
                ...predefinedStyles,
                transform: `translateY(-${Math.max(size / 3, 2)}px)`,
                boxShadow: `0px ${size / 2}px ${size}px 0px ${hexToRgba(color, intensity)}`,
            };
        case "scale":
            return { ...predefinedStyles, transform: `scale(${1 + intensity * 0.15})` };
        case "border_highlight":
            return {
                ...predefinedStyles,
                boxShadow: `0 0 0 ${Math.max(size / 6, 2)}px ${hexToRgba(color, intensity)}`,
            };
        case "pulse":
            return {
                ...predefinedStyles,
                ["--kefi-hover-pulse-color" as any]: hexToRgba(color, Math.max(intensity, 0.15)),
            } as React.CSSProperties;
        default:
            return { ...predefinedStyles };
    }
};

const getHoverEffectClassName = (
    bundlesDesign: VolumeConfigInterface["bundles_design"] | undefined,
    isHovered: boolean,
): string => (isHovered && bundlesDesign?.hover_effect === "pulse" ? styles.hoverPulse : "");

// this is horizontal like (horizontal offers stack)
export const OfferCard = ({
    offer,
    index,
    active,
    setActive,
    bundleConfig,
    variants,
    product,
    appConfigContext,
    standAlone,
    targetForm,
    setFreeGiftSelectedVariants,
    availableQuantity = 0,
    isProductAvailable,
    setCurrentActiveCardPrice,
    setCurrentActiveCardOriginalPrice,
    setBundleProductSelectedVariants,
    selectedSellingPlan = "",
    onVariantsChange,
    setUpsellSelectedVariants,
}: {
    variants: VariantsObj[];
    offer: OfferInterface;
    index: number;
    bundleConfig: VolumeConfigInterface;
    active: number;
    setActive: Dispatch<SetStateAction<number>>;
    product: ProductObj;
    appConfigContext: TStoreContext;
    unavailableSelected: number[];
    standAlone: boolean;
    targetForm?: any;
    setFreeGiftSelectedVariants: Dispatch<SetStateAction<VariantsObj[]>>;
    availableQuantity: number;
    isProductAvailable: boolean;
    setCurrentActiveCardPrice: Dispatch<SetStateAction<number>>;
    setCurrentActiveCardOriginalPrice?: Dispatch<SetStateAction<number>>;
    setBundleProductSelectedVariants: any;
    selectedSellingPlan?: string;
    onVariantsChange?: (variants: VariantsObj[]) => void;
    setUpsellSelectedVariants?: Dispatch<SetStateAction<VariantsObj[]>>;
}) => {
    const validQuantity = Math.max(typeof offer?.quantity === "number" && offer.quantity > 0 ? offer.quantity : 1, 0);
    const [selectedVariants, setSelectedVariants] = useState<VariantsObj[]>([]);
    const [lastChangedIndex, setLastChangedIndex] = useState(0);
    const [disableCard, setDisableCard] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { money_format, currency } = appConfigContext;

    const handleVariantsChange = (i: number) => (updated: VariantsObj[]) => {
        setLastChangedIndex(i);
        setSelectedVariants(updated);
    };

    const mainVariantImage =
        selectedVariants[lastChangedIndex]?.image || selectedVariants.find((v: any) => v?.image)?.image;
    const mainProductImage = mainVariantImage
        ? mainVariantImage?.originalSrc || (mainVariantImage as any).src
        : undefined;

    const { variantId, sellingPlan } = useShopifyProductState(standAlone);
    const effectiveSellingPlan = selectedSellingPlan || sellingPlan;

    useEffect(() => {
        if (targetForm && active == index) {
            product.selectedVariants = [...selectedVariants.map((v) => ({ ...v, selling_plan: effectiveSellingPlan }))];
        }
        if (active == index && onVariantsChange) {
            onVariantsChange(selectedVariants);
        }
    }, [selectedVariants, active, effectiveSellingPlan, index, onVariantsChange]);

    useEffect(() => {
        setSelectedVariants(
            getInitialSelectedVariants({
                quantity: validQuantity,
                productId: product.id,
                variants,
                defaultVariantTypes: bundleConfig.defaultVariantTypes,
            }),
        );
        setLastChangedIndex(0);
    }, [variants, validQuantity, bundleConfig.defaultVariantTypes]);

    // useEffect(() => {
    //     setFreeGiftSelectedVariants(() => {
    //         const temp = offer?.free_gifts.forEach((pd) => {
    //             temp.push();
    //         }, {});
    //     });
    // }, [offer.free_gifts]);

    useEffect(() => {
        if (!standAlone) return;

        if (bundleConfig.show_variant_selector) return;

        const currVar = variants.find((v: any) => v.id.split("/").pop() === variantId);
        if (!currVar) return;
        setSelectedVariants((prev: any) => prev.map(() => ({ ...currVar })));
    }, [variantId, variants]);

    useEffect(() => {
        if (validQuantity > availableQuantity && !(isProductAvailable && availableQuantity <= 0)) {
            setDisableCard(true);
        } else {
            setDisableCard(false);
        }
    }, [availableQuantity, active, validQuantity]);

    // Subscription discount details
    const subscription = product?.subscriptions?.find((s: any) => s.id === effectiveSellingPlan);
    const subDiscountValue = subscription?.discountValue || 0;
    const subDiscountType = subscription?.discountType || "none";

    // Prepaid logic
    const intervalRank: Record<string, number> = {
        DAY: 1,
        WEEK: 2,
        MONTH: 3,
        YEAR: 4,
    };

    let isSubPrepaid = false;
    if (subscription?.billingPolicy && subscription?.deliveryPolicy) {
        const billing = subscription.billingPolicy;
        const delivery = subscription.deliveryPolicy;

        const billingRank = intervalRank[billing.interval];
        const deliveryRank = intervalRank[delivery.interval];

        if (billingRank > deliveryRank) {
            isSubPrepaid = true;
        } else if (billingRank === deliveryRank) {
            isSubPrepaid = billing.intervalCount > delivery.intervalCount;
        }
    }

    const subIntervalCount = subscription?.billingPolicy?.intervalCount || 1;

    const bundleProductsOriginalPrice =
        offer?.bundle_products?.reduce((acc, p: ProductObj) => {
            const sv = (p.selectedVariants || []).reduce((v, variant) => {
                const priceToUse = bundleConfig?.use_compare_at_price
                    ? variant?.compareAtPrice || variant?.price
                    : variant?.price;

                return v + (isNaN(+priceToUse) ? 0 : +priceToUse);
            }, 0);

            return acc + sv;
        }, 0) || 0;

    const bundleProductsDiscountedPrice =
        offer?.bundle_products?.reduce((acc, p: ProductObj) => {
            const variants = p.selectedVariants || [];
            const totalVariantPrice = variants.reduce((sum, variant) => {
                const priceToUse = bundleConfig?.apply_discount_on_compare_price
                    ? variant?.compareAtPrice || variant?.price
                    : variant?.price;

                return sum + (isNaN(+priceToUse) ? 0 : +priceToUse);
            }, 0);

            let finalPrice = 0;
            finalPrice = getFinalPrice(p.discountType, p.discountValue || 0, totalVariantPrice);

            return acc + (isNaN(+finalPrice) ? 0 : +finalPrice);
        }, 0) || 0;

    // Calculate the original price without any discounts (using compareAtPrice if enabled)
    const originalPrice = selectedVariants.reduce((acc, variant) => {
        const priceToUse = bundleConfig?.use_compare_at_price ? variant?.compareAtPrice : variant?.price;
        return acc + +priceToUse;
    }, 0);

    // Calculate the final price without any discount
    let finalOriginalPrice = getFinalPrice("none", offer.discount_value || 0, originalPrice);

    // Calculate base price after subscription discount
    const basePriceAfterSub = selectedVariants.reduce((acc, variant) => {
        const price = bundleConfig?.apply_discount_on_compare_price ? +variant?.compareAtPrice : +variant?.price;
        let priceAfterSub = price;
        if (bundleConfig.enable_subscription_integration) {
            if (subDiscountType === "percentage") {
                priceAfterSub = price * (1 - subDiscountValue / 100);
            } else if (subDiscountType === "flat") {
                priceAfterSub = Math.max(0, price - subDiscountValue);
            } else if (subDiscountType === "fixed") {
                priceAfterSub = subDiscountValue;
            }
        }
        return acc + (isNaN(priceAfterSub) ? 0 : priceAfterSub);
    }, 0);

    // Calculate the final price based on the bundle discount applied on top of sub-discounted price
    let finalDiscountedPrice = getFinalPrice(offer.discount_type, offer.discount_value || 0, basePriceAfterSub);

    // Apply subscription frequency multiplier if a selling plan is selected
    if (effectiveSellingPlan && bundleConfig.enable_subscription_integration) {
        if (isSubPrepaid) {
            // Only prepaid plans should multiply
            // When the original price is lower than the subscription overriden base price, use the base price to calculate the correct discount
            finalOriginalPrice = Math.max(finalOriginalPrice * subIntervalCount, basePriceAfterSub * subIntervalCount);
            if (offer.discount_type === "flat") {
                finalDiscountedPrice = basePriceAfterSub * subIntervalCount - (offer.discount_value || 0);
            } else if (offer.discount_type === "percentage") {
                finalDiscountedPrice *= subIntervalCount;
            }
        } else {
            // Normal subscription → NO multiplication
            finalOriginalPrice = Math.max(finalOriginalPrice, basePriceAfterSub);
            if (offer.discount_type === "flat") {
                finalDiscountedPrice = basePriceAfterSub - (offer.discount_value || 0);
            }
        }
    }
    finalDiscountedPrice += bundleProductsDiscountedPrice;
    finalOriginalPrice += bundleProductsOriginalPrice;

    // Check if the final discounted price is less than the original price
    const isDiscounted = finalDiscountedPrice < finalOriginalPrice;

    // Combined discount percentage for UI display
    const combinedDiscountPercentage =
        finalOriginalPrice > 0
            ? Math.round(((finalOriginalPrice - finalDiscountedPrice) / finalOriginalPrice) * 100)
            : 0;

    const displayTag = useMemo(() => {
        return getParsedLabel(offer?.tag, {
            finalOriginalPrice,
            finalDiscountedPrice,
            combinedDiscountPercentage,
            variantCount: selectedVariants.length,
            money_format,
            currency,
        });
    }, [offer?.tag, finalOriginalPrice, finalDiscountedPrice, combinedDiscountPercentage, selectedVariants.length]);

    const displayDiscountLabel = useMemo(() => {
        return getParsedLabel(offer?.discount_label, {
            finalOriginalPrice,
            finalDiscountedPrice,
            combinedDiscountPercentage,
            variantCount: selectedVariants.length,
            money_format,
            currency,
        });
    }, [
        offer?.discount_label,
        finalOriginalPrice,
        finalDiscountedPrice,
        combinedDiscountPercentage,
        selectedVariants.length,
    ]);

    // Get the price label based on whether unit price should be shown or not
    const priceLabel = getPriceLabel(
        bundleConfig.show_unit_price ? finalDiscountedPrice / selectedVariants.length : finalDiscountedPrice,
        money_format,
        currency,
    );

    // Get the original price label if the item has a discount
    const originalPriceLabel = getPriceLabel(
        bundleConfig.show_unit_price ? finalOriginalPrice / selectedVariants.length : finalOriginalPrice,
        money_format,
        currency,
    );

    useMemo(() => {
        if (active !== index) return;

        setCurrentActiveCardPrice(finalDiscountedPrice);
        setCurrentActiveCardOriginalPrice?.(finalOriginalPrice);
    }, [
        active,
        index,
        setCurrentActiveCardPrice,
        setCurrentActiveCardOriginalPrice,
        finalDiscountedPrice,
        finalOriginalPrice,
    ]);

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;

    const renderPrice = (
        <>
            {isProductAvailable ? (
                <div
                    className={styles.priceRow}
                    style={{
                        flexDirection:
                            bundleConfig.selected_template_volume == AvailableVolumeTemplates.VERTICAL_MODERN
                                ? "row"
                                : "column",
                        gap:
                            bundleConfig.selected_template_volume == AvailableVolumeTemplates.VERTICAL_MODERN
                                ? "10px"
                                : "2px",
                    }}
                >
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productPrice">
                        <div
                            className={styles.offeredPrice}
                            style={{
                                color: bundleConfig?.bundles_design.highlighted_price_tag_color,
                                fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size,
                                fontWeight: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design.highlighted_price_tag_style?.split(" ")[1],
                                width: "max-content",
                            }}
                        >
                            {finalDiscountedPrice <= 0 ? bundleConfig?.free_text || "Free" : priceLabel}
                            <span
                                style={{
                                    fontSize: bundleConfig?.bundles_design.highlighted_price_tag_size - 2,
                                }}
                            >
                                {bundleConfig?.show_unit_price
                                    ? bundleConfig?.show_unit_price_text
                                        ? bundleConfig?.show_unit_price_text
                                        : "/unit"
                                    : ""}
                            </span>
                        </div>
                    </InspectorEditableBlock>

                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productCompareAtPrice">
                        <div
                            className={styles.cartTotal}
                            style={{
                                color: bundleConfig?.bundles_design.product_price_color,
                                fontSize: bundleConfig?.bundles_design.product_price_size,
                                textDecoration: "line-through",
                                fontWeight: bundleConfig?.bundles_design.product_price_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design.product_price_style?.split(" ")[1],
                                visibility: isDiscounted ? "visible" : "hidden",
                                display: isDiscounted ? "block" : "none",
                            }}
                        >
                            {originalPriceLabel}
                        </div>
                    </InspectorEditableBlock>
                </div>
            ) : (
                <span className={styles.outOfStock}>{bundleConfig?.no_stock_text || "Out of stock!"}</span>
            )}
        </>
    );

    const showDiscountLabel =
        !!offer?.discount_label?.length ||
        (offer?.discount_label_type === "custom" && !!offer?.discount_label_custom_img);

    const discountLabel = (
        <>
            {showDiscountLabel && (
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="discountLabel">
                    {offer?.discount_label_type !== "none" ? (
                        renderDiscountLabelContent(offer, displayDiscountLabel, bundleConfig)
                    ) : (
                        <div
                            className={styles.discountLabel}
                            style={{
                                color: bundleConfig?.bundles_design.discount_label_color,
                                fontWeight: bundleConfig?.bundles_design.discount_label_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design.discount_label_style?.split(" ")[1],
                                fontSize: bundleConfig.bundles_design.discount_label_font_size,
                                backgroundColor: bundleConfig?.bundles_design.discount_label_background_color,
                            }}
                        >
                            {renderDiscountLabelContent(offer, displayDiscountLabel, bundleConfig)}
                        </div>
                    )}
                </InspectorEditableBlock>
            )}
        </>
    );

    const badgeDiscountPercentage = (() => {
        if (finalOriginalPrice <= 0) return "0";
        const raw = ((finalOriginalPrice - finalDiscountedPrice) / finalOriginalPrice) * 100;
        const clamped = Math.min(Math.max(raw, 0), 100);
        const rounded = Math.round(clamped * 10) / 10;
        return rounded % 1 === 0 ? `${rounded}` : rounded.toFixed(1);
    })();

    const legacyElegantBadgeContent: Record<string, string> = {
        numbers: "{number}",
        quantity: "{quantity}",
        discount: "{discount_in_percent}",
        none: "",
    };

    const elegantBadgeContent = (() => {
        const rawTemplate = bundleConfig?.bundles_design?.elegant_badge_content;
        const template =
            rawTemplate === undefined ? "{number}" : (legacyElegantBadgeContent[rawTemplate] ?? rawTemplate);
        if (!template.trim()) return null;
        return template
            .replace(/{number}/g, String(index + 1))
            .replace(/{quantity}/g, String(validQuantity))
            .replace(/{discount_in_percent}/g, `${badgeDiscountPercentage}%`);
    })();

    const badgeFontSize = (() => {
        const len = String(elegantBadgeContent ?? "").length;
        if (len <= 2) return 15;
        if (len === 3) return 13;
        if (len === 4) return 11;
        return 10;
    })();

    const discountTag = (
        <>
            {!!displayTag?.length && (
                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="discountTag">
                    <div
                        className={styles.offerTag}
                        style={{
                            color: bundleConfig?.bundles_design.special_text_color,
                            fontWeight: bundleConfig?.bundles_design.special_text_style?.split(" ")[0],
                            fontStyle: bundleConfig?.bundles_design.special_text_style?.split(" ")[1],
                            fontSize: bundleConfig.bundles_design.special_text_font_size,
                            backgroundColor:
                                bundleConfig.bundles_design.special_text_bg_opacity ||
                                bundleConfig.selected_template_volume === AvailableVolumeTemplates.VERTICAL_ELEGANT
                                    ? "transparent"
                                    : bundleConfig?.bundles_design.special_text_background_color,
                            padding:
                                bundleConfig.bundles_design.special_text_bg_opacity ||
                                bundleConfig.selected_template_volume === AvailableVolumeTemplates.VERTICAL_ELEGANT
                                    ? "3px 0"
                                    : "4px 5px",
                        }}
                    >
                        {displayTag}
                    </div>
                </InspectorEditableBlock>
            )}
        </>
    );

    const imgOption = (
        <>
            {offer?.allow_img && offer?.img_url && (
                <img
                    src={offer?.img_url}
                    alt="Product img"
                    style={{
                        objectFit: offer?.img_fit ?? "cover",
                        width: offer?.img_size + "px",
                        height: offer?.img_size + "px",
                        borderRadius: offer?.img_radius,
                    }}
                />
            )}
        </>
    );

    const radioButtonOption = (
        <>
            <span
                onClick={(e) =>
                    handleActive(
                        index,
                        setActive,
                        setFreeGiftSelectedVariants,
                        setBundleProductSelectedVariants,
                        setUpsellSelectedVariants,
                        offer,
                        e.currentTarget,
                        bundleConfig.show_free_gift_popper,
                    )
                }
                className={`${styles.customChecked} ${active == index && styles._checked}`}
                style={{
                    background: active == index ? bundleConfig?.bundles_design?.product_border_color : "transparent",
                    outlineColor: bundleConfig?.bundles_design?.product_border_color,
                    width: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                    height: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                    minWidth: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                    minHeight: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                }}
            ></span>
        </>
    );

    const displayTitle = useMemo(() => {
        const t = offer?.offer_title || "";

        if (!t.includes("{discount}")) {
            return t;
        }

        if (bundleConfig.enable_subscription_integration) {
            if (offer.discount_type === DISCOUNT_TYPES.PERCENTAGE) {
                return t.replace(/{discount}/g, combinedDiscountPercentage + "%");
            }

            if (offer.discount_type === DISCOUNT_TYPES.FLAT || offer.discount_type === DISCOUNT_TYPES.FIXED) {
                const combinedAbsoluteDiscount = Math.round(finalOriginalPrice - finalDiscountedPrice);
                const combinedDiscountString = getDiscountValueWithSignStringSubscription(
                    offer.discount_type,
                    combinedAbsoluteDiscount,
                    currency,
                    money_format,
                );
                return t.replace(/{discount}/g, combinedDiscountString);
            }
        }

        // fallback: replace {discount} with the original offer discount value
        const fallbackString = getDiscountValueWithSignStringSubscription(
            offer.discount_type,
            offer.discount_value,
            currency,
            money_format,
        );
        return t.replace(/{discount}/g, fallbackString);
    }, [
        offer.offer_title,
        combinedDiscountPercentage,
        bundleConfig.enable_subscription_integration,
        offer.discount_type,
        offer.discount_value,
        finalOriginalPrice,
        finalDiscountedPrice,
        currency,
        money_format,
    ]);
    const titleComp = (
        <>
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productTitle" wrapWithDiv={true}>
                <div
                    className={styles.offerTitle}
                    style={{
                        fontSize: bundleConfig?.bundles_design.product_name_size,
                        color: bundleConfig?.bundles_design?.product_name_color,
                        fontWeight: bundleConfig?.bundles_design?.product_name_style?.split(" ")[0],
                        fontStyle: bundleConfig?.bundles_design?.product_name_style?.split(" ")[1],
                    }}
                >
                    {displayTitle ?? offer?.offer_title}
                </div>
            </InspectorEditableBlock>
        </>
    );

    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product,
        bundleConfig: bundleConfig || {},
    });

    const renderVariantSelector = ({ padding }: { padding: string }) => {
        return (
            <>
                {active == index && bundleConfig.show_variant_selector && variants.length > 1 && (
                    <div
                        className={styles.variantSelectorWrapper}
                        style={{ padding }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <>
                            {(() => {
                                const length =
                                    bundleConfig.bundle_product_level === "product"
                                        ? Number.isInteger(+selectedVariants?.length) && +selectedVariants?.length > 0
                                            ? +selectedVariants?.length
                                            : 1
                                        : 1;

                                if (length <= 0) {
                                    return <p>No variants available</p>;
                                }

                                return new Array(length)
                                    .fill(1)
                                    .map((_, i) =>
                                        userShopname === "typsy-beauty.myshopify.com" ? (
                                            <Swatches
                                                bundlesDesign={bundleConfig?.bundles_design}
                                                key={i}
                                                variants={product.variants}
                                                handleChange={handleVariantsChange(i)}
                                                selected={selectedVariants}
                                                options={product.options || []}
                                                productCardIndex={i}
                                                standAlone={standAlone}
                                            />
                                        ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                            <CustomSwatches
                                                key={i}
                                                variants={product.variants}
                                                handleChange={handleVariantsChange(i)}
                                                selected={selectedVariants}
                                                options={product.options || []}
                                                productCardIndex={i}
                                                standAlone={standAlone}
                                                metafield={effectiveMetafield}
                                                bundlesDesign={bundleConfig?.bundles_design}
                                                bundleConfig={bundleConfig}
                                                hideLabel={!!bundleConfig?.show_option_labels_once && i !== 0}
                                                hideSerialNumber={!!bundleConfig?.hide_item_numbers}
                                            />
                                        ) : (
                                            <VariantSelectorV2
                                                key={i}
                                                variants={product.variants}
                                                handleChange={handleVariantsChange(i)}
                                                selected={selectedVariants}
                                                options={product.options || []}
                                                productCardIndex={i}
                                                standAlone={standAlone}
                                                metafield={effectiveMetafield}
                                                bundlesDesign={bundleConfig?.bundles_design}
                                                bundleConfig={bundleConfig}
                                                hideLabel={!!bundleConfig?.show_option_labels_once && i !== 0}
                                                hideSerialNumber={!!bundleConfig?.hide_item_numbers}
                                            />
                                        ),
                                    );
                            })()}
                        </>
                    </div>
                )}
            </>
        );
    };

    return (
        <Tooltip
            text={
                disableCard && availableQuantity >= 1
                    ? bundleConfig?.out_of_stock_disable_text.replace(/{itemCount}/g, availableQuantity?.toString())
                    : ""
            }
            position={"top"}
            tooltipWrapperStyle={{
                width: "100%",
                ...(index === 0 && offer?.discount_label?.length > 0 && { marginTop: "5px" }),
            }}
        >
            <InspectorEditableBlock
                bundleType={bundleConfig?.bundle_type}
                settingKey="productCard"
                tagsKey={product?.tags?.toString()}
                productId={getProductId(product?.id)}
            >
                <div
                    className={styles.offerCardOuter}
                    style={{ ...(disableCard && { pointerEvents: "none", opacity: 0.6 }) }}
                >
                    {discountLabel}
                    <div
                        key={offer?.offer_title}
                        className={`${styles.offerCardBox} ${getHoverEffectClassName(bundleConfig?.bundles_design, isHovered)}`}
                        style={{
                            border: `${
                                active == index
                                    ? bundleConfig?.bundles_design?.border_width + 1
                                    : bundleConfig?.bundles_design?.border_width
                            }px solid ${
                                active == index
                                    ? hexToRgba(bundleConfig?.bundles_design?.product_border_color, 1)
                                    : hexToRgba(bundleConfig?.bundles_design?.product_border_color, 0.6)
                            }`,
                            boxShadow:
                                active == index
                                    ? `0px 2px 9px 0px ${hexToRgba(
                                          bundleConfig?.bundles_design?.product_border_color,
                                          0.25,
                                      )}`
                                    : "none",
                            borderRadius: bundleConfig?.bundles_design?.border_radius,
                            backgroundColor:
                                active == index
                                    ? bundleConfig?.bundles_design.product_background_color
                                    : bundleConfig?.bundles_design?.bundle_widget_bg,
                            ...getHoverEffectStyle(bundleConfig?.bundles_design, isHovered),
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onClick={(e) =>
                            handleActive(
                                index,
                                setActive,
                                setFreeGiftSelectedVariants,
                                setBundleProductSelectedVariants,
                                setUpsellSelectedVariants,
                                offer,
                                e.currentTarget,
                                bundleConfig.show_free_gift_popper,
                            )
                        }
                    >
                        {bundleConfig.selected_template_volume == AvailableVolumeTemplates.VERTICAL_BASIC && (
                            <>
                                <div className={styles.mainCard}>
                                    <div className={styles.mainCardChild}>
                                        {radioButtonOption}
                                        {imgOption}
                                        <div className={styles.offerInfoColumn}>
                                            <div className={styles.offerTitleRow}>
                                                {titleComp}
                                                {discountTag}
                                            </div>
                                            {!!offer?.show_quantity_selector && (
                                                <QBQuantitySelector
                                                    minQuantity={+offer.quantity}
                                                    selectedVariants={selectedVariants}
                                                    setSelectedVariants={setSelectedVariants}
                                                    bundleConfig={bundleConfig}
                                                />
                                            )}
                                        </div>
                                        {/* {discountLabel} */}
                                    </div>
                                    <div>{renderPrice}</div>
                                </div>
                            </>
                        )}

                        {bundleConfig.selected_template_volume == AvailableVolumeTemplates.VERTICAL_MODERN && (
                            <>
                                <div className={styles.mainCard}>
                                    <div className={styles.mainCardChild}>
                                        {radioButtonOption}
                                        {imgOption}
                                        <div className={styles.offerInfoColumnGap8}>
                                            {titleComp}
                                            {renderPrice}
                                            {!!offer?.show_quantity_selector && (
                                                <QBQuantitySelector
                                                    minQuantity={+offer.quantity}
                                                    selectedVariants={selectedVariants}
                                                    setSelectedVariants={setSelectedVariants}
                                                    bundleConfig={bundleConfig}
                                                />
                                            )}
                                        </div>
                                        {/* {discountLabel} */}
                                    </div>

                                    <div>{discountTag}</div>
                                </div>
                            </>
                        )}

                        {bundleConfig.selected_template_volume == AvailableVolumeTemplates.VERTICAL_CLASSIC && (
                            <>
                                <div className={styles.mainCard}>
                                    <div className={styles.mainCardChild}>
                                        {radioButtonOption}
                                        {imgOption}
                                        <div className={styles.offerInfoColumnGap8}>
                                            {titleComp}
                                            {discountTag}
                                            {!!offer?.show_quantity_selector && (
                                                <QBQuantitySelector
                                                    minQuantity={+offer.quantity}
                                                    selectedVariants={selectedVariants}
                                                    setSelectedVariants={setSelectedVariants}
                                                    bundleConfig={bundleConfig}
                                                />
                                            )}
                                        </div>
                                        {/* {discountLabel} */}
                                    </div>
                                    <div>{renderPrice}</div>
                                </div>
                            </>
                        )}

                        {bundleConfig.selected_template_volume == AvailableVolumeTemplates.VERTICAL_ELEGANT && (
                            <>
                                <div className={styles.elegantRow}>
                                    <span
                                        className={styles.elegantAccentBar}
                                        style={{
                                            backgroundColor: bundleConfig?.bundles_design?.product_border_color,
                                            opacity: active === index ? 1 : 0,
                                        }}
                                    />

                                    {elegantBadgeContent !== null && (
                                        <InspectorEditableBlock
                                            bundleType={bundleConfig?.bundle_type}
                                            settingKey="elegantBadge"
                                        >
                                            <div
                                                className={styles.elegantBadge}
                                                style={{
                                                    background: `linear-gradient(135deg, ${
                                                        bundleConfig?.bundles_design?.product_border_color
                                                    }, ${
                                                        bundleConfig?.bundles_design?.add_to_cart_bg_color ||
                                                        bundleConfig?.bundles_design?.product_border_color
                                                    })`,
                                                    fontSize: badgeFontSize,
                                                }}
                                            >
                                                {elegantBadgeContent}
                                            </div>
                                        </InspectorEditableBlock>
                                    )}

                                    {imgOption}

                                    <div className={styles.elegantInfo}>
                                        {titleComp}
                                        {discountTag}
                                        {!!offer?.show_quantity_selector && (
                                            <QBQuantitySelector
                                                minQuantity={+offer.quantity}
                                                selectedVariants={selectedVariants}
                                                setSelectedVariants={setSelectedVariants}
                                                bundleConfig={bundleConfig}
                                            />
                                        )}
                                    </div>

                                    <div className={styles.elegantPriceBlock}>{renderPrice}</div>
                                </div>
                            </>
                        )}

                        {offer.offer_type === "product_bundle" && (
                            <ProductBundleComponent
                                products={offer.bundle_products}
                                bundleConfig={bundleConfig}
                                standAlone={standAlone}
                                setBundleProductSelectedVariants={setBundleProductSelectedVariants}
                                money_format={money_format}
                                currency={currency}
                                offer={offer}
                                mainProduct={product}
                                mainProductImage={mainProductImage}
                                mainProductOriginalPrice={finalOriginalPrice - bundleProductsOriginalPrice}
                                mainProductDiscountedPrice={finalDiscountedPrice - bundleProductsDiscountedPrice}
                                renderVariantSelector={renderVariantSelector}
                                isCurrentActiveOffer={active === index}
                                active={active}
                                index={index}
                            />
                        )}

                        {/* Variant Selector */}
                        {offer.offer_type != "product_bundle" && renderVariantSelector({ padding: "0 12px 12px 12px" })}

                        {offer?.allow_free_gifts &&
                            offer?.hasOwnProperty("free_gifts") &&
                            offer?.free_gifts?.length > 0 && (
                                <FreeGiftComponent
                                    products={offer?.free_gifts}
                                    bundleConfig={
                                        {
                                            ...bundleConfig,
                                            free_gift_text: offer?.free_gift_text,
                                            free_gift_template: offer?.free_gift_template || "old",
                                            free_gift_show_price: offer?.free_gift_show_price,
                                            bundles_design: {
                                                ...bundleConfig.bundles_design,
                                                add_to_cart_border_radius: bundleConfig?.bundles_design.border_radius,
                                            },
                                        } as any
                                    }
                                    standAlone={standAlone}
                                    targetForm={targetForm}
                                    active={active}
                                    index={index}
                                    setFreeGiftSelectedVariants={setFreeGiftSelectedVariants}
                                    money_format={money_format}
                                    currency={currency}
                                />
                            )}

                        {offer?.allow_upsell_products && offer?.upsell_products?.length > 0 && (
                            <UpsellComponent
                                products={offer.upsell_products}
                                offer={offer}
                                bundleConfig={bundleConfig}
                                standAlone={standAlone}
                                money_format={money_format}
                                currency={currency}
                                isCurrentActiveOffer={active === index}
                                setUpsellSelectedVariants={setUpsellSelectedVariants}
                                active={active}
                            />
                        )}
                    </div>
                </div>
            </InspectorEditableBlock>
        </Tooltip>
    );
};

// this is vertical
export const OfferCardV2 = ({
    offer,
    index,
    active,
    setActive,
    bundleConfig,
    variants,
    product,
    appConfigContext,
    targetForm,
    standAlone,
    setFreeGiftSelectedVariants,
    availableQuantity,
    isProductAvailable,
    setCurrentActiveCardPrice,
    setCurrentActiveCardOriginalPrice,
    setBundleProductSelectedVariants,
    selectedSellingPlan = "",
    onVariantsChange,
    setUpsellSelectedVariants,
}: {
    variants: VariantsObj[];
    offer: OfferInterface;
    index: number;
    bundleConfig: VolumeConfigInterface;
    active: number;
    setActive: Dispatch<SetStateAction<number>>;
    product: ProductObj;
    appConfigContext: TStoreContext;
    unavailableSelected: number[];
    standAlone: boolean;
    targetForm?: any;
    setFreeGiftSelectedVariants: Dispatch<SetStateAction<VariantsObj[]>>;
    availableQuantity: number;
    isProductAvailable: boolean;
    setCurrentActiveCardPrice: Dispatch<SetStateAction<number>>;
    setCurrentActiveCardOriginalPrice?: Dispatch<SetStateAction<number>>;
    setBundleProductSelectedVariants: any;
    selectedSellingPlan?: string;
    onVariantsChange?: (variants: VariantsObj[]) => void;
    setUpsellSelectedVariants?: Dispatch<SetStateAction<VariantsObj[]>>;
}) => {
    const validQuantity = Math.max(typeof offer?.quantity === "number" && offer.quantity > 0 ? offer.quantity : 1, 0);
    const [selectedVariants, setSelectedVariants] = useState<VariantsObj[]>([]);
    const { money_format, currency } = appConfigContext;
    const [disableCard, setDisableCard] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const { variantId, sellingPlan } = useShopifyProductState(standAlone);
    const effectiveSellingPlan = selectedSellingPlan || sellingPlan;

    useEffect(() => {
        if (targetForm && active == index) {
            product.selectedVariants = [...selectedVariants.map((v) => ({ ...v, selling_plan: effectiveSellingPlan }))];
        }
        if (active == index && onVariantsChange) {
            onVariantsChange(selectedVariants);
        }
    }, [selectedVariants, active, effectiveSellingPlan, index, onVariantsChange]);

    useEffect(() => {
        setSelectedVariants(
            getInitialSelectedVariants({
                quantity: validQuantity,
                productId: product.id,
                variants,
                defaultVariantTypes: bundleConfig.defaultVariantTypes,
            }),
        );
    }, [variants, validQuantity, bundleConfig.defaultVariantTypes]);

    useEffect(() => {
        if (!standAlone) return;

        if (bundleConfig.show_variant_selector) return;

        const currVar = variants.find((v: any) => v.id.split("/").pop() === variantId);
        if (!currVar) return;
        setSelectedVariants((prev: any) => prev.map(() => ({ ...currVar })));
    }, [variantId, variants]);

    useEffect(() => {
        if (validQuantity > availableQuantity && !(isProductAvailable && availableQuantity <= 0)) {
            setDisableCard(true);
        } else {
            setDisableCard(false);
        }
    }, [availableQuantity, active, validQuantity]);

    // Subscription discount details
    const subscription = product?.subscriptions?.find((s: any) => s.id === effectiveSellingPlan);
    const subDiscountValue = subscription?.discountValue || 0;
    const subDiscountType = subscription?.discountType || "none";

    const bundleProductsOriginalPrice =
        offer?.bundle_products?.reduce((acc, p: ProductObj) => {
            const sv = (p.selectedVariants || []).reduce((v, variant) => {
                const priceToUse = bundleConfig?.use_compare_at_price
                    ? variant?.compareAtPrice || variant?.price
                    : variant?.price;

                return v + (isNaN(+priceToUse) ? 0 : +priceToUse);
            }, 0);

            return acc + sv;
        }, 0) || 0;

    const bundleProductsDiscountedPrice =
        offer?.bundle_products?.reduce((acc, p: ProductObj) => {
            const variants = p.selectedVariants || [];
            const totalVariantPrice = variants.reduce((sum, variant) => {
                const priceToUse = bundleConfig?.apply_discount_on_compare_price
                    ? variant?.compareAtPrice || variant?.price
                    : variant?.price;

                return sum + (isNaN(+priceToUse) ? 0 : +priceToUse);
            }, 0);

            let finalPrice = 0;
            finalPrice = getFinalPrice(p.discountType, p.discountValue || 0, totalVariantPrice);

            return acc + (isNaN(+finalPrice) ? 0 : +finalPrice);
        }, 0) || 0;

    // Prepaid logic
    const intervalRank: Record<string, number> = {
        DAY: 1,
        WEEK: 2,
        MONTH: 3,
        YEAR: 4,
    };

    let isSubPrepaid = false;
    if (subscription?.billingPolicy && subscription?.deliveryPolicy) {
        const billing = subscription.billingPolicy;
        const delivery = subscription.deliveryPolicy;

        const billingRank = intervalRank[billing.interval];
        const deliveryRank = intervalRank[delivery.interval];

        if (billingRank > deliveryRank) {
            isSubPrepaid = true;
        } else if (billingRank === deliveryRank) {
            isSubPrepaid = billing.intervalCount > delivery.intervalCount;
        }
    }

    const subIntervalCount = subscription?.billingPolicy?.intervalCount || 1;
    // Calculate the original price without any discounts (using compareAtPrice if enabled)
    const originalPrice = selectedVariants.reduce((acc, variant) => {
        const priceToUse = bundleConfig?.use_compare_at_price ? variant?.compareAtPrice : variant?.price;
        return acc + +priceToUse;
    }, 0);

    // Calculate the final price without any discount
    let finalOriginalPrice = getFinalPrice("none", offer.discount_value || 0, originalPrice);

    // Calculate base price after subscription discount
    const basePriceAfterSub = selectedVariants.reduce((acc, variant) => {
        const price = bundleConfig?.apply_discount_on_compare_price ? +variant?.compareAtPrice : +variant?.price;
        let priceAfterSub = price;
        if (bundleConfig.enable_subscription_integration) {
            if (subDiscountType === "percentage") {
                priceAfterSub = price * (1 - subDiscountValue / 100);
            } else if (subDiscountType === "flat") {
                priceAfterSub = Math.max(0, price - subDiscountValue);
            } else if (subDiscountType === "fixed") {
                priceAfterSub = subDiscountValue;
            }
        }
        return acc + (isNaN(priceAfterSub) ? 0 : priceAfterSub);
    }, 0);

    // Calculate the final price based on the bundle discount applied on top of sub-discounted price
    let finalDiscountedPrice = getFinalPrice(offer.discount_type, offer.discount_value || 0, basePriceAfterSub);

    // Apply subscription frequency multiplier if a selling plan is selected
    if (effectiveSellingPlan && bundleConfig.enable_subscription_integration) {
        if (isSubPrepaid) {
            // Only prepaid plans should multiply
            // When the original price is lower than the subscription overriden base price, use the base price to calculate the correct discount
            finalOriginalPrice = Math.max(finalOriginalPrice * subIntervalCount, basePriceAfterSub * subIntervalCount);
            if (offer.discount_type === "flat") {
                finalDiscountedPrice = basePriceAfterSub * subIntervalCount - (offer.discount_value || 0);
            } else if (offer.discount_type === "percentage") {
                finalDiscountedPrice *= subIntervalCount;
            }
        } else {
            // Normal subscription → NO multiplication
            finalOriginalPrice = Math.max(finalOriginalPrice, basePriceAfterSub);
            if (offer.discount_type === "flat") {
                finalDiscountedPrice = basePriceAfterSub - (offer.discount_value || 0);
            }
        }
    }
    finalDiscountedPrice += bundleProductsDiscountedPrice;
    finalOriginalPrice += bundleProductsOriginalPrice;

    // Check if the final discounted price is less than the original price
    const isDiscounted = finalDiscountedPrice < finalOriginalPrice;

    // Combined discount percentage for UI display
    const combinedDiscountPercentage =
        finalOriginalPrice > 0
            ? Math.round(((finalOriginalPrice - finalDiscountedPrice) / finalOriginalPrice) * 100)
            : 0;

    const displayTag = useMemo(() => {
        return getParsedLabel(offer?.tag, {
            finalOriginalPrice,
            finalDiscountedPrice,
            combinedDiscountPercentage,
            variantCount: selectedVariants.length,
            money_format,
            currency,
        });
    }, [offer?.tag, finalOriginalPrice, finalDiscountedPrice, combinedDiscountPercentage, selectedVariants.length]);

    const displayDiscountLabel = useMemo(() => {
        return getParsedLabel(offer?.discount_label, {
            finalOriginalPrice,
            finalDiscountedPrice,
            combinedDiscountPercentage,
            variantCount: selectedVariants.length,
            money_format,
            currency,
        });
    }, [
        offer?.discount_label,
        finalOriginalPrice,
        finalDiscountedPrice,
        combinedDiscountPercentage,
        selectedVariants.length,
    ]);

    // Get the price label based on whether unit price should be shown or not
    const priceLabel = getPriceLabel(
        bundleConfig.show_unit_price ? finalDiscountedPrice / selectedVariants.length : finalDiscountedPrice,
        money_format,
        currency,
    );

    // Get the original price label if the item has a discount
    const originalPriceLabel = getPriceLabel(
        bundleConfig.show_unit_price ? finalOriginalPrice / selectedVariants.length : finalOriginalPrice,
        money_format,
        currency,
    );

    useMemo(() => {
        if (active !== index) return;

        setCurrentActiveCardPrice(finalDiscountedPrice);
        setCurrentActiveCardOriginalPrice?.(finalOriginalPrice);
    }, [
        active,
        index,
        setCurrentActiveCardPrice,
        finalDiscountedPrice,
        setCurrentActiveCardOriginalPrice,
        finalOriginalPrice,
    ]);

    const hasDiscountLabel = offer?.discount_label_type !== "none" || !!offer?.discount_label?.length;

    const displayTitle = useMemo(() => {
        const t = offer?.offer_title || "";

        if (!t.includes("{discount}")) {
            return t;
        }

        if (bundleConfig.enable_subscription_integration) {
            if (offer.discount_type === DISCOUNT_TYPES.PERCENTAGE) {
                return t.replace(/{discount}/g, combinedDiscountPercentage + "%");
            }

            if (offer.discount_type === DISCOUNT_TYPES.FLAT || offer.discount_type === DISCOUNT_TYPES.FIXED) {
                const combinedAbsoluteDiscount = Math.round(finalOriginalPrice - finalDiscountedPrice);
                const combinedDiscountString = getDiscountValueWithSignStringSubscription(
                    offer.discount_type,
                    combinedAbsoluteDiscount,
                    currency,
                    money_format,
                );
                return t.replace(/{discount}/g, combinedDiscountString);
            }
        }

        // fallback: replace {discount} with the original offer discount value
        const fallbackString = getDiscountValueWithSignStringSubscription(
            offer.discount_type,
            offer.discount_value,
            currency,
            money_format,
        );
        return t.replace(/{discount}/g, fallbackString);
    }, [
        offer.offer_title,
        combinedDiscountPercentage,
        bundleConfig.enable_subscription_integration,
        offer.discount_type,
        offer.discount_value,
        finalOriginalPrice,
        finalDiscountedPrice,
        currency,
        money_format,
    ]);

    return (
        <Tooltip
            text={
                disableCard && availableQuantity >= 1
                    ? bundleConfig?.out_of_stock_disable_text.replace(/{itemCount}/g, availableQuantity?.toString())
                    : ""
            }
            position={"top"}
            tooltipWrapperStyle={{ width: "100%" }}
        >
            <InspectorEditableBlock
                bundleType={bundleConfig?.bundle_type}
                settingKey="productCard"
                tagsKey={product?.tags?.toString()}
                productId={getProductId(product?.id)}
            >
                <div className={styles.offerCardV2Outer}>
                    <div
                        key={offer?.offer_title}
                        className={`${styles.OfferCardWrapperV2} ${getHoverEffectClassName(
                            bundleConfig?.bundles_design,
                            isHovered,
                        )}`}
                        style={{
                            border: `${bundleConfig?.bundles_design?.border_width}px solid ${
                                active == index
                                    ? hexToRgba(bundleConfig?.bundles_design?.product_border_color, 1)
                                    : hexToRgba(bundleConfig?.bundles_design?.product_border_color, 0.6)
                            }`,
                            boxShadow:
                                active == index
                                    ? `0px 2px 9px 0px ${hexToRgba(
                                          bundleConfig?.bundles_design?.product_border_color,
                                          0.25,
                                      )}`
                                    : "none",
                            borderRadius: bundleConfig?.bundles_design?.border_radius,
                            backgroundColor:
                                active == index
                                    ? bundleConfig?.bundles_design.product_background_color
                                    : bundleConfig?.bundles_design?.bundle_widget_bg,
                            ...(disableCard && { pointerEvents: "none", opacity: 0.6 }),
                            ...getHoverEffectStyle(bundleConfig?.bundles_design, isHovered),
                            ...(bundleConfig.selected_template_volume === AvailableVolumeTemplates.HORIZONTAL_BASIC &&
                                offer.discount_label_type == "none" && {
                                    overflow: "hidden",
                                }),
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        onClick={(e) =>
                            handleActive(
                                index,
                                setActive,
                                setFreeGiftSelectedVariants,
                                setBundleProductSelectedVariants,
                                setUpsellSelectedVariants,
                                offer,
                                e.currentTarget,
                                bundleConfig.show_free_gift_popper,
                            )
                        }
                    >
                        {bundleConfig.selected_template_volume == "horizontal_modern" && hasDiscountLabel && (
                            <div className={styles.discountLabelWrapper}>
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="discountLabel"
                                >
                                    {offer?.discount_label_type !== "none" ? (
                                        renderDiscountLabelContent(offer, displayDiscountLabel, bundleConfig, true)
                                    ) : (
                                        <div
                                            className={styles.discountLabelModernV2}
                                            style={{
                                                color: bundleConfig?.bundles_design.discount_label_color,
                                                fontWeight:
                                                    bundleConfig?.bundles_design.discount_label_style?.split(" ")[0],
                                                fontStyle:
                                                    bundleConfig?.bundles_design.discount_label_style?.split(" ")[1],
                                                fontSize: bundleConfig.bundles_design.discount_label_font_size,
                                                backgroundColor:
                                                    bundleConfig?.bundles_design.discount_label_background_color,
                                            }}
                                        >
                                            {renderDiscountLabelContent(offer, displayDiscountLabel, bundleConfig)}
                                        </div>
                                    )}
                                </InspectorEditableBlock>
                            </div>
                        )}

                        {bundleConfig.selected_template_volume == "horizontal_basic" && hasDiscountLabel && (
                            <div className={styles.discountLabelWrapper}>
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="discountLabel"
                                >
                                    {offer?.discount_label_type !== "none" ? (
                                        renderDiscountLabelContent(offer, displayDiscountLabel, bundleConfig, true)
                                    ) : (
                                        <div
                                            className={styles.discountLabelBasicV2}
                                            style={{
                                                color: bundleConfig?.bundles_design.discount_label_color,
                                                fontWeight:
                                                    bundleConfig?.bundles_design.discount_label_style?.split(" ")[0],
                                                fontStyle:
                                                    bundleConfig?.bundles_design.discount_label_style?.split(" ")[1],
                                                fontSize: bundleConfig.bundles_design.discount_label_font_size,
                                                backgroundColor:
                                                    bundleConfig?.bundles_design.discount_label_background_color,
                                                padding: "5px",
                                                whiteSpace: "break-spaces",
                                                display: "block",
                                            }}
                                        >
                                            {renderDiscountLabelContent(offer, displayDiscountLabel, bundleConfig)}
                                        </div>
                                    )}
                                </InspectorEditableBlock>
                            </div>
                        )}

                        <div className={styles.mainContainerV2}>
                            <div
                                className={styles.contentWrapperV2}
                                style={{
                                    paddingBlock: bundleConfig?.volume_discounts?.some(
                                        (o: OfferInterface) =>
                                            (o?.discount_label_type === "custom" &&
                                                !!o?.discount_label_custom_img?.length) ||
                                            (o?.discount_label_type !== "custom" && !!o?.discount_label?.length),
                                    )
                                        ? bundleConfig.selected_template_volume ===
                                          AvailableVolumeTemplates.HORIZONTAL_BASIC
                                            ? "34px 10px"
                                            : bundleConfig.selected_template_volume ===
                                                AvailableVolumeTemplates.HORIZONTAL_MODERN
                                              ? "20px 10px"
                                              : 12
                                        : 12,
                                }}
                            >
                                <div className={styles.offerWrapperV2}>
                                    <div className={styles.offerTitleContainerV2}>
                                        <span
                                            onClick={(e) =>
                                                handleActive(
                                                    index,
                                                    setActive,
                                                    setFreeGiftSelectedVariants,
                                                    setBundleProductSelectedVariants,
                                                    setUpsellSelectedVariants,
                                                    offer,
                                                    e.currentTarget,
                                                    bundleConfig.show_free_gift_popper,
                                                )
                                            }
                                            className={`${styles.customChecked} ${active == index && styles._checked}`}
                                            style={{
                                                background:
                                                    active == index
                                                        ? bundleConfig?.bundles_design?.product_border_color
                                                        : "transparent",
                                                outlineColor: bundleConfig?.bundles_design?.product_border_color,
                                                width: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                                                height: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                                                minWidth: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                                                minHeight: bundleConfig?.bundles_design?.product_name_size - 4 + "px",
                                            }}
                                        ></span>

                                        <div className={styles.offerTitleGroupV2}>
                                            {offer?.allow_img && offer?.img_url && (
                                                <img
                                                    src={offer?.img_url}
                                                    alt="Product img"
                                                    className={styles.offerImgV2}
                                                    style={{
                                                        objectFit: offer?.img_fit ?? "cover",
                                                        width: offer?.img_size + "px",
                                                        height: offer?.img_size + "px",
                                                        borderRadius: offer?.img_radius,
                                                    }}
                                                />
                                            )}
                                            <InspectorEditableBlock
                                                bundleType={bundleConfig?.bundle_type}
                                                settingKey="productTitle"
                                                wrapWithDiv={true}
                                            >
                                                <div
                                                    className={styles.offerTitleV2}
                                                    style={{
                                                        fontSize: bundleConfig?.bundles_design.product_name_size,
                                                        color: bundleConfig?.bundles_design?.product_name_color,
                                                        fontWeight:
                                                            bundleConfig?.bundles_design?.product_name_style?.split(
                                                                " ",
                                                            )[0],
                                                        fontStyle:
                                                            bundleConfig?.bundles_design?.product_name_style?.split(
                                                                " ",
                                                            )[1],
                                                    }}
                                                >
                                                    {displayTitle ?? offer?.offer_title}
                                                </div>
                                            </InspectorEditableBlock>

                                            {!!displayTag?.length && (
                                                <InspectorEditableBlock
                                                    bundleType={bundleConfig?.bundle_type}
                                                    settingKey="discountTag"
                                                >
                                                    <div
                                                        style={{
                                                            color: bundleConfig?.bundles_design.special_text_color,
                                                            fontWeight:
                                                                bundleConfig?.bundles_design.special_text_style?.split(
                                                                    " ",
                                                                )[0],
                                                            fontStyle:
                                                                bundleConfig?.bundles_design.special_text_style?.split(
                                                                    " ",
                                                                )[1],
                                                            fontSize:
                                                                bundleConfig.bundles_design.special_text_font_size,
                                                            backgroundColor: bundleConfig.bundles_design
                                                                .special_text_bg_opacity
                                                                ? "transparent"
                                                                : bundleConfig?.bundles_design
                                                                      .special_text_background_color,
                                                        }}
                                                        className={`${styles.offerTag} ${styles.offerTagCenter}`}
                                                    >
                                                        {displayTag}
                                                    </div>
                                                </InspectorEditableBlock>
                                            )}
                                        </div>
                                    </div>
                                    {!!offer?.show_quantity_selector && (
                                        <QBQuantitySelector
                                            minQuantity={+offer.quantity}
                                            selectedVariants={selectedVariants}
                                            setSelectedVariants={setSelectedVariants}
                                            bundleConfig={bundleConfig}
                                        />
                                    )}
                                    <div className={styles.offerPricingContainerV2}>
                                        {isProductAvailable ? (
                                            <>
                                                <InspectorEditableBlock
                                                    bundleType={bundleConfig?.bundle_type}
                                                    settingKey="productPrice"
                                                >
                                                    <div
                                                        style={{
                                                            color: bundleConfig?.bundles_design
                                                                .highlighted_price_tag_color,
                                                            fontSize:
                                                                bundleConfig?.bundles_design.highlighted_price_tag_size,
                                                            fontWeight:
                                                                bundleConfig?.bundles_design.highlighted_price_tag_style?.split(
                                                                    " ",
                                                                )[0],
                                                            fontStyle:
                                                                bundleConfig?.bundles_design.highlighted_price_tag_style?.split(
                                                                    " ",
                                                                )[1],
                                                        }}
                                                        className={styles.originalProductPrice}
                                                    >
                                                        {finalDiscountedPrice <= 0 ? "Free" : priceLabel}
                                                        <span
                                                            style={{
                                                                fontSize:
                                                                    bundleConfig?.bundles_design
                                                                        .highlighted_price_tag_size - 2,
                                                            }}
                                                        >
                                                            {bundleConfig?.show_unit_price
                                                                ? bundleConfig?.show_unit_price_text
                                                                    ? bundleConfig?.show_unit_price_text
                                                                    : "/unit"
                                                                : ""}
                                                        </span>
                                                    </div>
                                                </InspectorEditableBlock>

                                                <InspectorEditableBlock
                                                    bundleType={bundleConfig?.bundle_type}
                                                    settingKey="productCompareAtPrice"
                                                >
                                                    <div
                                                        style={{
                                                            color: bundleConfig?.bundles_design.product_price_color,
                                                            fontSize: bundleConfig?.bundles_design.product_price_size,
                                                            fontWeight:
                                                                bundleConfig?.bundles_design.product_price_style?.split(
                                                                    " ",
                                                                )[0],
                                                            fontStyle:
                                                                bundleConfig?.bundles_design.product_price_style?.split(
                                                                    " ",
                                                                )[1],
                                                            visibility: isDiscounted ? "visible" : "hidden",
                                                        }}
                                                        className={styles.compareAtProductPrice}
                                                    >
                                                        {originalPriceLabel}
                                                    </div>
                                                </InspectorEditableBlock>
                                            </>
                                        ) : (
                                            <span className={styles.outOfStock}>
                                                {bundleConfig?.no_stock_text || "Out of stock!"}
                                            </span>
                                        )}
                                    </div>

                                    {offer.offer_type === "product_bundle" && (
                                        <ProductBundleComponent
                                            products={offer.bundle_products}
                                            bundleConfig={bundleConfig}
                                            standAlone={standAlone}
                                            setBundleProductSelectedVariants={setBundleProductSelectedVariants}
                                            money_format={money_format}
                                            currency={currency}
                                            offer={offer}
                                            mainProduct={product}
                                            mainProductOriginalPrice={finalOriginalPrice - bundleProductsOriginalPrice}
                                            mainProductDiscountedPrice={
                                                finalDiscountedPrice - bundleProductsDiscountedPrice
                                            }
                                            renderVariantSelector={() => {}}
                                            isCurrentActiveOffer={active === index}
                                            active={active}
                                            index={index}
                                        />
                                    )}

                                    {offer?.allow_upsell_products && offer?.upsell_products?.length > 0 && (
                                        <UpsellComponent
                                            products={offer.upsell_products}
                                            offer={offer}
                                            bundleConfig={bundleConfig}
                                            standAlone={standAlone}
                                            money_format={money_format}
                                            currency={currency}
                                            isCurrentActiveOffer={active === index}
                                            setUpsellSelectedVariants={setUpsellSelectedVariants}
                                            active={active}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </InspectorEditableBlock>
        </Tooltip>
    );
};

export const VolumePreview = ({
    bundleConfig,
    standAlone = false,
    pId = 0,
    appConfigContext,
    targetForm,
    setActiveQuantity,
}: Props_VolumePreview) => {
    const [active, setActive] = useState<number>(
        bundleConfig?.hasOwnProperty("default_offer")
            ? bundleConfig?.default_offer >= 0
                ? bundleConfig?.default_offer
                : -1
            : 0,
    );

    const [freeGiftSelectedVariants, setFreeGiftSelectedVariants] = useState<VariantsObj[]>([]);
    const [bundleProductSelectedVariants, setBundleProductSelectedVariants] = useState<VariantsObj[]>([]);
    const [upsellSelectedVariants, setUpsellSelectedVariants] = useState<VariantsObj[]>([]);

    const [unavailableSelected] = useState<{
        [key: number]: number[];
    }>({});
    const [currentActiveCardPrice, setCurrentActiveCardPrice] = useState(0);
    const [currentActiveCardOriginalPrice, setCurrentActiveCardOriginalPrice] = useState(0);
    const [selectedSellingPlan, setSelectedSellingPlan] = useState<string>("");
    const [activeSelectedVariants, setActiveSelectedVariants] = useState<VariantsObj[]>([]);

    useEffect(() => {
        // check if property exists or not
        setActive(
            bundleConfig?.hasOwnProperty("default_offer")
                ? bundleConfig?.default_offer >= 0
                    ? bundleConfig?.default_offer
                    : -1
                : 0,
        );
    }, [bundleConfig?.default_offer]);

    // do not remove this, it runs and handles shop's variant selection
    useCustomLocation();
    const isProductAvailable = useMemo(() => {
        return bundleConfig?.products?.[pId as any]?.variants?.some(
            (vv) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
        );
    }, [bundleConfig?.products[pId as any], bundleConfig?.bundle_product_level]);
    const availableQuantity = useMemo(() => {
        return bundleConfig?.products?.[pId as any]?.variants?.reduce((sum, vv) => {
            return sum + (typeof vv.quantityAvailable === "number" ? vv.quantityAvailable : 0);
        }, 0);
    }, [bundleConfig?.products[pId as any], active]);

    useEffect(() => {
        if (active < 0) {
            window.dispatchEvent(
                new CustomEvent("disableCtaChanged", {
                    detail: { disabled: true, availableQuantity: 0 },
                }),
            );
            return;
        }

        if (bundleConfig?.volume_discounts?.length && !(availableQuantity <= 0 && isProductAvailable)) {
            let validOffersCount = 0;
            bundleConfig?.volume_discounts.forEach((offer) => {
                if (offer.quantity <= availableQuantity) {
                    validOffersCount = ++validOffersCount;
                }
            });

            window.dispatchEvent(
                new CustomEvent("disableCtaChanged", {
                    detail: { disabled: validOffersCount == 0, availableQuantity },
                }),
            );
        }
    }, [bundleConfig?.volume_discounts, availableQuantity, active, isProductAvailable]);

    const activeOfferQuantity = bundleConfig?.volume_discounts?.[active]?.quantity;
    useEffect(() => {
        if (active < 0) return;
        setActiveQuantity && setActiveQuantity(activeOfferQuantity || 1);
    }, [activeOfferQuantity, active]);

    if (!bundleConfig) return;
    const { currency, money_format } = appConfigContext;
    const isSpecificSelection =
        bundleConfig?.volume_product_selection === "specific" && bundleConfig.products.length === 0;

    const isHorizontalTemplate =
        bundleConfig?.selected_template_volume === AvailableVolumeTemplates.HORIZONTAL_BASIC ||
        bundleConfig?.selected_template_volume === AvailableVolumeTemplates.HORIZONTAL_MODERN;

    const gridColumns =
        bundleConfig?.volume_discounts?.length === 4
            ? "repeat(2, 1fr)"
            : bundleConfig?.volume_discounts?.length < 3
              ? `repeat(${bundleConfig?.volume_discounts?.length}, 1fr)`
              : "repeat(3, 1fr)";

    const selectedDiscount = bundleConfig?.volume_discounts?.[active] || { discount_value: null, discount_type: null };

    const offer = bundleConfig?.volume_discounts?.[active] || {};
    const validQuantity = Math.max(typeof offer?.quantity === "number" && offer.quantity > 0 ? offer.quantity : 1, 0);
    typeof validQuantity;

    const freeGiftValue = freeGiftSelectedVariants?.reduce((total, sv) => total + (+sv?.price || 0), 0) || 0;
    const includedFreeGiftValue = bundleConfig?.savings_summary?.include_free_gift_value ? freeGiftValue : 0;
    const savedAmount = currentActiveCardOriginalPrice - currentActiveCardPrice + includedFreeGiftValue;
    const savedPercentageBase = currentActiveCardOriginalPrice + includedFreeGiftValue;
    const savedPercentage = savedPercentageBase > 0 ? Math.round((savedAmount / savedPercentageBase) * 100) : 0;

    return (
        <BundlePreviewWrapper
            bundleConfig={{
                ...bundleConfig,
                products: bundleConfig.products.slice(0, 1),
                free_gifts: [
                    {
                        selectedVariants: freeGiftSelectedVariants,
                        productProperties: {
                            [ATTRIBUTE_KEY_SHORTCODES.__volumeFreeGift]: 1,
                            [ATTRIBUTE_KEY_SHORTCODES.__offerIndex]: active,
                        },
                    },
                ],
                bundle_products: [
                    {
                        selectedVariants: bundleProductSelectedVariants,
                        productProperties: {
                            [ATTRIBUTE_KEY_SHORTCODES.__volumeBundleProduct]: 1,
                            [ATTRIBUTE_KEY_SHORTCODES.__offerIndex]: active,
                        },
                    },
                ],
                upsell_products: [
                    {
                        selectedVariants: upsellSelectedVariants,
                        productProperties: {
                            [ATTRIBUTE_KEY_SHORTCODES.__volumeUpsellProduct]: 1,
                            [ATTRIBUTE_KEY_SHORTCODES.__offerIndex]: active,
                        },
                    },
                ],
                // @ts-expect-error required
                discount_value: selectedDiscount.discount_value,
                discount_type: selectedDiscount.discount_type,
            }}
            standAlone={standAlone}
            totalPrice={currentActiveCardPrice}
            currency={currency}
            money_format={money_format}
        >
            <>
                {bundleConfig?.show_countdown_timer && bundleConfig?.countdown_timer_text && (
                    <CountdownTimer
                        countdown_timer_minutes={bundleConfig?.countdown_timer_minutes}
                        countdown_timer_text={bundleConfig?.countdown_timer_text}
                        timer_color={bundleConfig?.bundles_design?.timer_color}
                        timer_text_color={bundleConfig?.bundles_design?.timer_text_color}
                        timer_background_color={bundleConfig?.bundles_design?.timer_background_color}
                        timer_font_style={bundleConfig?.bundles_design?.timer_font_style}
                        timer_font_size={bundleConfig?.bundles_design?.timer_font_size}
                        bundleType={bundleConfig?.bundle_type}
                    />
                )}

                <>
                    <div
                        className={styles.products}
                        id="uniqueProductsKefi"
                        style={{
                            display: isSpecificSelection ? "flex" : isHorizontalTemplate ? "grid" : "flex",
                            flexDirection: isSpecificSelection ? "column" : isHorizontalTemplate ? "row" : "column",
                            gridTemplateColumns: gridColumns,
                            overflowX: isHorizontalTemplate ? "auto" : "visible",
                            overflowY: "visible",
                            padding: isHorizontalTemplate ? "10px 10px 15px" : "10px 0",
                            ...([
                                AvailableVolumeTemplates.HORIZONTAL_MODERN,
                                AvailableVolumeTemplates.HORIZONTAL_BASIC,
                            ].includes(bundleConfig?.selected_template_volume) &&
                                bundleConfig?.volume_discounts?.some(
                                    (o: OfferInterface) =>
                                        (o?.discount_label_type === "custom" &&
                                            !!o?.discount_label_custom_img?.length) ||
                                        (o?.discount_label_type !== "custom" && !!o?.discount_label?.length),
                                ) && {
                                    paddingTop: 22,
                                }),
                        }}
                    >
                        {bundleConfig?.products?.length && bundleConfig?.volume_discounts?.length ? (
                            bundleConfig?.volume_discounts?.map((offer, index) => {
                                return bundleConfig.selected_template_volume ==
                                    AvailableVolumeTemplates.VERTICAL_BASIC ||
                                    bundleConfig.selected_template_volume == AvailableVolumeTemplates.VERTICAL_MODERN ||
                                    bundleConfig.selected_template_volume ==
                                        AvailableVolumeTemplates.VERTICAL_CLASSIC ||
                                    bundleConfig.selected_template_volume ==
                                        AvailableVolumeTemplates.VERTICAL_ELEGANT ? (
                                    <OfferCard
                                        key={index}
                                        index={index}
                                        offer={offer}
                                        bundleConfig={bundleConfig}
                                        active={active}
                                        setActive={setActive}
                                        variants={
                                            bundleConfig.bundle_product_level == "product"
                                                ? bundleConfig?.products[pId as any]?.variants
                                                : bundleConfig?.products.map((p) => p.variants[0])
                                        }
                                        product={bundleConfig?.products[pId as any]}
                                        appConfigContext={appConfigContext}
                                        unavailableSelected={unavailableSelected[index] || []}
                                        standAlone={standAlone || false}
                                        targetForm={targetForm}
                                        setFreeGiftSelectedVariants={setFreeGiftSelectedVariants}
                                        availableQuantity={availableQuantity}
                                        isProductAvailable={isProductAvailable}
                                        setCurrentActiveCardPrice={setCurrentActiveCardPrice}
                                        setCurrentActiveCardOriginalPrice={setCurrentActiveCardOriginalPrice}
                                        setBundleProductSelectedVariants={setBundleProductSelectedVariants}
                                        selectedSellingPlan={selectedSellingPlan}
                                        onVariantsChange={setActiveSelectedVariants}
                                        setUpsellSelectedVariants={setUpsellSelectedVariants}
                                    />
                                ) : (
                                    <OfferCardV2
                                        key={index}
                                        index={index}
                                        offer={offer}
                                        bundleConfig={bundleConfig}
                                        active={active}
                                        setActive={setActive}
                                        variants={
                                            bundleConfig.bundle_product_level == "product"
                                                ? bundleConfig?.products[pId as any]?.variants
                                                : bundleConfig?.products.map((p) => p.variants[0])
                                        }
                                        product={bundleConfig?.products[pId as any]}
                                        appConfigContext={appConfigContext}
                                        unavailableSelected={unavailableSelected[index] || []}
                                        standAlone={standAlone || false}
                                        targetForm={targetForm}
                                        setFreeGiftSelectedVariants={setFreeGiftSelectedVariants}
                                        availableQuantity={availableQuantity}
                                        isProductAvailable={isProductAvailable}
                                        setCurrentActiveCardPrice={setCurrentActiveCardPrice}
                                        setCurrentActiveCardOriginalPrice={setCurrentActiveCardOriginalPrice}
                                        setBundleProductSelectedVariants={setBundleProductSelectedVariants}
                                        selectedSellingPlan={selectedSellingPlan}
                                        onVariantsChange={setActiveSelectedVariants}
                                        setUpsellSelectedVariants={setUpsellSelectedVariants}
                                    />
                                );
                            })
                        ) : (
                            <>
                                <OfferCardShimmer />
                            </>
                        )}
                    </div>
                    <SubscriptionBox
                        product={bundleConfig?.products?.[pId as any]}
                        selectedSellingPlan={selectedSellingPlan}
                        setSelectedSellingPlan={setSelectedSellingPlan}
                        bundleConfig={bundleConfig}
                        activeSelectedVariants={activeSelectedVariants}
                    />
                </>
                {(bundleConfig?.selected_template_volume == AvailableVolumeTemplates.HORIZONTAL_BASIC ||
                    bundleConfig?.selected_template_volume == AvailableVolumeTemplates.HORIZONTAL_MODERN) &&
                    active >= 0 &&
                    (bundleConfig?.volume_product_selection != "specific" ||
                        (bundleConfig?.volume_product_selection == "specific" && bundleConfig?.products.length > 0)) &&
                    active < bundleConfig.volume_discounts.length &&
                    bundleConfig?.volume_discounts[active]?.allow_free_gifts &&
                    bundleConfig?.volume_discounts[active]?.hasOwnProperty("free_gifts") &&
                    bundleConfig?.volume_discounts[active]?.free_gifts?.length > 0 && (
                        <div className={styles.freeGiftWrapper}>
                            <FreeGiftComponent
                                products={bundleConfig?.volume_discounts[active]?.free_gifts}
                                bundleConfig={
                                    {
                                        ...bundleConfig,
                                        free_gift_text: bundleConfig?.volume_discounts[active]?.free_gift_text,
                                        free_gift_template:
                                            bundleConfig?.volume_discounts[active]?.free_gift_template || "old",
                                        free_gift_show_price:
                                            bundleConfig?.volume_discounts[active]?.free_gift_show_price,
                                        bundles_design: {
                                            ...bundleConfig.bundles_design,
                                            add_to_cart_border_radius: bundleConfig?.bundles_design.border_radius,
                                        },
                                    } as any
                                }
                                standAlone={standAlone}
                                targetForm={targetForm}
                                index={active}
                                active={active}
                                setFreeGiftSelectedVariants={setFreeGiftSelectedVariants}
                                money_format={money_format}
                                currency={currency}
                            />
                        </div>
                    )}
                <SavingsSummaryBar
                    savingsSummary={bundleConfig?.savings_summary}
                    bundlesDesign={bundleConfig?.bundles_design}
                    savedAmount={savedAmount}
                    savedPercentage={savedPercentage}
                    totalPrice={currentActiveCardPrice}
                    money_format={money_format}
                    currency={currency}
                    bundleType={bundleConfig?.bundle_type}
                />
            </>
        </BundlePreviewWrapper>
    );
};

export default VolumeBundle;
